// ✅ UPDATED: UpdateClasses (adds Regulation + builds Subjects from codedegreesubjectmap)
// What changed:
// 1) Added Regulation everywhere (filters + table + new row + sample excel)
// 2) Subjects dropdown is now driven by codedegreesubjectmap using:
//    SubCampusCode + DegreeCode + BranchCode + Regulation + Semester
// 3) (Optional) Supports MapID coming from backend (kept harmless if not present)
// Notes:
// - You said you still want to store Degree/Branch/Sem/Reg/Subject etc in classes table (kept as-is)

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Paper,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Snackbar,
  Alert,
  Typography,
  Button,
  Autocomplete,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Stack,
  FormControlLabel,
  Switch,
  Chip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import UploadIcon from "@mui/icons-material/Upload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const UpdateClasses = () => {
  // ✅ Theme constants (same as ManageUsers)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const dangerOutlinedBtnSx = {
    ...outlinedBtnSx,
    borderColor: "rgba(244,67,54,0.35)",
    color: "rgba(244,67,54,0.95)",
    "&:hover": {
      borderColor: "rgba(244,67,54,0.55)",
      backgroundColor: "rgba(244,67,54,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== Original state/logic =====
  const [campusCode, setCampusCode] = useState("");
  const [rows, setRows] = useState([]);
  const [teachersBySub, setTeachersBySub] = useState({});
  const [classrepresentativesBySub, setClassRepresentativesBySub] = useState({});
  const [subjects, setSubjects] = useState([]);

  const [academicyear, setAcademicYear] = useState([]);
  const [subCampusCodes, setSubCampusCodes] = useState([]);
  const [degreeCodes, setDegreeCodes] = useState([]);
  const [branchCodes, setBranchCodes] = useState([]);
  const [regulations, setRegulations] = useState([]); // ✅ NEW
  const [semesters, setSemesters] = useState([]);

  const [tab, setTab] = useState(0);

  const [copyForm, setCopyForm] = useState({
    FromAcademicYear: "",
    ToAcademicYear: "",
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    SubjectCode: [],
    Section: [],
    CopyTeacher: true,
    CopyRoom: true,
    CopyClassRep: false,
    ActivateNew: true,
  });
  
  const [copying, setCopying] = useState(false);
  const [bulkCreateReport, setBulkCreateReport] = useState(null);
  const [copyReport, setCopyReport] = useState(null);

  // Manage Classes add/edit popup (same interaction pattern as ManageRoleClassTeacher).
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [classDialogMode, setClassDialogMode] = useState("add");
  const [classDialogEditId, setClassDialogEditId] = useState(null);
  const [classSaving, setClassSaving] = useState(false);

  const [editRowId, setEditRowId] = useState(null);
  const [editOriginalRow, setEditOriginalRow] = useState(null);

  const [degreeSubjectMapLookups, setDegreeSubjectMapLookups] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    Subjects: [],
  });

  const [newRow, setNewRow] = useState({
    AcademicYear: "",
    SubCampusCode: "",
    DegreeCode: "",
    BranchCode: "",
    Regulation: "", // ✅ NEW
    Semester: "",
    SubjectCode: "",
    SubjectNameCode: "",
    SubjectName: "",
    Section: "",
    RoomNumber: "",
    TeacherID: "",
    ClassRepresentativeID: "",
    IsActive: 0,
    // MapID: "", // optional if your backend returns it; keep commented unless you want to show it
  });

  const [bulkCreateForm, setBulkCreateForm] = useState({
    AcademicYear: "",
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    Semester: [],
    SubjectCode: [],
    Section: [],
    RoomNumber: "",
    TeacherID: "",
    ClassRepresentativeID: "",
    IsActive: 1,
  });

  const [bulkSubjects, setBulkSubjects] = useState([]);
  const [sectionOptionsFromTable, setSectionOptionsFromTable] = useState([]);
  const [bulkCreating, setBulkCreating] = useState(false);

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  let empFetchCancel;

  const fetchEmployees = async (query) => {
    if (!query || query.length < 2) {
      setEmployeeOptions([]);
      return;
    }
    if (empFetchCancel) empFetchCancel.cancel();
    empFetchCancel = axios.CancelToken.source();
    setEmpLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/Employees/search`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` },
        cancelToken: empFetchCancel.token,
      });
      const arr = res.data?.employees || res.data || [];
      setEmployeeOptions(
        arr.map((e) => ({
          id: e.EmployeeID ?? e.id,
          name: e.EmployeeName ?? e.name,
          label: `${e.EmployeeName ?? e.name} (${e.EmployeeID ?? e.id})`,
        }))
      );
    } catch (e) {
      if (!axios.isCancel(e)) console.error(e);
    } finally {
      setEmpLoading(false);
    }
  };

  // ===== Bulk teacher / class representative assignment =====
  const emptyBulkAssignFilters = {
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [],
    SemesterCode: [],
    Section: [],
    SubjectCode: [],
    SubjectNameCode: [],
    TeacherName: [],
    ClassRepresentativeName: [],
    IsActive: ["Active"],
  };

  const [bulkAssignFilters, setBulkAssignFilters] = useState(emptyBulkAssignFilters);
  const [bulkAssignFiltersApplied, setBulkAssignFiltersApplied] = useState(false);
  const [bulkAssignmentType, setBulkAssignmentType] = useState("TEACHER");
  const [bulkAssigneeId, setBulkAssigneeId] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkAssignSelectedRows, setBulkAssignSelectedRows] = useState([]);
  const [bulkAssignPage, setBulkAssignPage] = useState(0);
  const [bulkAssignRowsPerPage, setBulkAssignRowsPerPage] = useState(100);

  // ===== Bulk delete classes =====
  const [bulkDeleteFilters, setBulkDeleteFilters] = useState(emptyBulkAssignFilters);
  const [bulkDeleteFiltersApplied, setBulkDeleteFiltersApplied] = useState(false);
  const [bulkDeleteSelectedRows, setBulkDeleteSelectedRows] = useState([]);
  const [bulkDeletePage, setBulkDeletePage] = useState(0);
  const [bulkDeleteRowsPerPage, setBulkDeleteRowsPerPage] = useState(100);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteReport, setBulkDeleteReport] = useState(null);
  
  const SELECT_ALL_VALUE = "__ALL__";
  
  const withSelectAll = (list = []) => [
    { label: "Select All", value: SELECT_ALL_VALUE, isSelectAll: true },
    ...list.map((x) =>
      typeof x === "object"
        ? x
        : { label: String(x), value: String(x) }
    ),
  ];
  
  const getOptionValue = (opt) => opt?.value ?? opt;
  const getOptionLabelText = (opt) => opt?.label ?? String(opt ?? "");
  
  const handleMultiSelectAll = (field, list) => (_, value) => {
    const hasSelectAll = value.some((x) => getOptionValue(x) === SELECT_ALL_VALUE);
  
    setBulkCreateForm((prev) => ({
      ...prev,
      [field]: hasSelectAll ? list.map((x) => getOptionValue(x)) : value.map(getOptionValue),
    }));
  };

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  // ✅ filtering gate + selection + bulk update
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]); // holds row.IDs

  const toNullable = (v) => {
  const s = String(v ?? "").trim();
  return s === "" ? null : s;
  };

  const normalizeClassPayload = (obj) => ({
    ...obj,
    RoomNumber: String(obj.RoomNumber ?? "").trim() || null, // empty => null (or keep "" if your DB allows)
    TeacherID: toNullable(obj.TeacherID),                    // "" => null
    ClassRepresentativeID: toNullable(obj.ClassRepresentativeID), // "" => null
  });


  // ─── helper: reload all classes ────────────────────────────────────────
  const fetchData = async () => {
    const res = await axios.get(`${API_URL}/api/GetExistingClasses/${campusCode}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setRows(res.data || []);
  };

  // ─── decode campusCode from JWT ───────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
    } catch {
      console.error("Failed to decode token");
    }
  }, [token]);

  const downloadSampleExcel = () => {
    const headers = [
      "AcademicYear",
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "Regulation", // ✅ NEW
      "Semester",
      "SubjectCode",
      "SubjectNameCode",
      "SubjectName",
      "Section",
      "RoomNumber",
      "TeacherID",
      "ClassRepresentativeID",
      "IsActive",
    ];

    const cc = campusCode || "CAMPUS01";

    const sampleRows = [
      {
        AcademicYear: "2025-26",
        SubCampusCode: "SC01",
        DegreeCode: "BTECH",
        BranchCode: "CSE",
        Regulation: "R20", // ✅ NEW
        Semester: "1",
        SubjectCode: "CS101",
        SubjectNameCode: "CS101N",
        SubjectName: "Programming for Problem Solving",
        Section: "A",
        RoomNumber: "B-201",
        TeacherID: "T1001",
        ClassRepresentativeID: "21A91A0501",
        IsActive: 1,
      },
    ];

    const normalizedRows = sampleRows.map((r) => {
      const obj = {};
      headers.forEach((h) => (obj[h] = r[h] ?? ""));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(normalizedRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(16, h.length + 2) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Classes");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `UpdateClasses_Sample_${cc}.xlsx`);
  };

  // ===== Filters =====
  const [filters, setFilters] = useState({
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [], // ✅ NEW
    SemesterCode: [],
    Section: [],
    SubjectCode: [],
    SubjectNameCode: [],
    TeacherName: [],
    ClassRepresentativeName: [],
    IsActive: ["Active"],
  });

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);

  const [options, setOptions] = useState({
    AcademicYear: [],
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    Regulation: [], // ✅ NEW
    SemesterCode: [],
    Section: [],
    SubjectNameCode: [],
    TeacherName: [],
    ClassRepresentativeName: [],
  });

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setOptions({
          AcademicYear: data?.AcademicYear || [],
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          Regulation: data?.Regulation || [], // ✅ NEW (backend must return)
          SemesterCode: (data?.SemesterCode || []).map(String),
          Section: (data?.Section || []).map(String),
          SubjectNameCode: data?.SubjectNameCode || [],
          TeacherName: data?.TeacherName || [],
          ClassRepresentativeName: data?.ClassRepresentativeName || [],
        });
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  // ─── initial load of classes + lookups ─────────────────────────────────
  useEffect(() => {
    if (!campusCode) return;

    axios
      .get(`${API_URL}/api/lookups/degreeSubjectMap`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { CampusCode: campusCode },
      })
      .then((res) => {
        setDegreeSubjectMapLookups({
          SubCampusCode: res.data?.SubCampusCode || [],
          DegreeCode: res.data?.DegreeCode || [],
          BranchCode: res.data?.BranchCode || [],
          Regulation: res.data?.Regulation || [],
          Semester: (res.data?.Semester || []).map(String),
          Subjects: res.data?.Subjects || [],
        });
      })
      .catch(() => {
        setToast({
          open: true,
          type: "error",
          msg: "Failed to load bulk create lookups.",
        });
      });
  }, [campusCode, token]);

  useEffect(() => {
    if (!campusCode) return;

    fetchData().catch(() => setError("Failed to load classes"));

    axios
      .get(`${API_URL}/api/UpdateAcademicYears/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setAcademicYear(res.data.academicyear || []))
      .catch(console.error);

    axios
      .get(`${API_URL}/api/UpdateClassesSections/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSectionOptionsFromTable(res.data.sections || []))
      .catch(console.error);

    axios
      .get(`${API_URL}/api/UpdateClassesValidateCampus/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSubCampusCodes(res.data.subcampuses || []))
      .catch(console.error);

    axios
      .get(`${API_URL}/api/UpdateClassesSemester/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSemesters(res.data.results || []))
      .catch(console.error);

    // ✅ NEW: regulations list (you can also fetch via lookups/all only; keeping dedicated endpoint optional)
    axios
      .get(`${API_URL}/api/UpdateClassesRegulations/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setRegulations(res.data.results || []))
      .catch(() => {
        // If you don't implement this endpoint, it won't break UI (Regulation options from lookups/all still works)
      });
  }, [campusCode, token]);

  useEffect(() => {
    if (!campusCode) return;
  
    const {
      SubCampusCode,
      DegreeCode,
      BranchCode,
      Regulation,
      Semester,
    } = bulkCreateForm;
  
    if (
      !SubCampusCode.length ||
      !DegreeCode.length ||
      !BranchCode.length ||
      !Regulation.length ||
      !Semester.length
    ) {
      setBulkSubjects([]);
      setBulkCreateForm((p) => ({ ...p, SubjectCode: [] }));
      return;
    }
  
    axios
      .get(`${API_URL}/api/lookups/degreeSubjectMap`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          CampusCode: campusCode,
          SubCampusCode: SubCampusCode.join(","),
          DegreeCode: DegreeCode.join(","),
          BranchCode: BranchCode.join(","),
          Regulation: Regulation.join(","),
          Semester: Semester.join(","),
        },
      })
      .then((res) => {
        setBulkSubjects(res.data?.Subjects || []);
        setBulkCreateForm((p) => ({ ...p, SubjectCode: [] }));
      })
      .catch(() => {
        setBulkSubjects([]);
        setBulkCreateForm((p) => ({ ...p, SubjectCode: [] }));
      });
  }, [
    campusCode,
    bulkCreateForm.SubCampusCode,
    bulkCreateForm.DegreeCode,
    bulkCreateForm.BranchCode,
    bulkCreateForm.Regulation,
    bulkCreateForm.Semester,
    token,
  ]);

  useEffect(() => {
    if (!rows?.length) return;

    const uniqueSubs = Array.from(
      new Set(rows.map((r) => (r?.SubCampusCode ?? "").trim()).filter(Boolean))
    );
    uniqueSubs.forEach((sc) => fetchTeachersForSub(sc));
    uniqueSubs.forEach((sc) => fetchClassRepresentativesForSub(sc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const fetchTeachersForSub = async (subCampusCode) => {
    const key = (subCampusCode ?? "").trim();
    if (!key || teachersBySub[key]) return;

    try {
      const res = await axios.get(
        `${API_URL}/api/RoleTeachers`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { SubCampusCode: key },
        }
      );
      const rawList = Array.isArray(res.data) ? res.data : res.data?.teachers || [];
      const list = rawList
        .filter((t) => !t.SubCampusCode || String(t.SubCampusCode).trim().toLowerCase() === key.toLowerCase())
        .map((t) => ({
          ...t,
          TeacherID: String(t.TeacherID ?? t.ID ?? t.id ?? t.EmployeeID ?? ""),
          TeacherName: t.TeacherName ?? t.Name ?? t.FullName ?? t.fullName ?? t.EmployeeName ?? "",
          EmployeeID: t.EmployeeID ?? t.employeeId ?? "",
          SubCampusCode: t.SubCampusCode ?? key,
        }));
      setTeachersBySub((prev) => ({ ...prev, [key]: list }));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchClassRepresentativesForSub = async (subCampusCode) => {
    const key = (subCampusCode ?? "").trim();
    if (!key || classrepresentativesBySub[key]) return;

    try {
      const res = await axios.get(
        `${API_URL}/api/RoleClassRepresentative`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { SubCampusCode: key },
        }
      );

      const rawList = Array.isArray(res.data) ? res.data : res.data?.classrepresentatives || [];
      const list = rawList
        .filter((t) => !t.SubCampusCode || String(t.SubCampusCode).trim().toLowerCase() === key.toLowerCase())
        .map((t) => ({
          ...t,
          ClassRepresentativeID: String(
            t.ClassRepresentativeID ?? t.ID ?? t.id ?? t.StudentID ?? ""
          ),
          StudentKey: t.StudentKey ?? null,
          SubCampusCode: t.SubCampusCode ?? key,
          ClassRepresentativeName:
            t.ClassRepresentativeName ??
            t.Name ??
            t.StudentName ??
            t.FullName ??
            t.fullName ??
            "",
          StudentID: t.StudentID ?? "",
        }));

      setClassRepresentativesBySub((prev) => ({ ...prev, [key]: list }));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDegrees = (sub) => {
    axios
      .get(`${API_URL}/api/UpdateClassesValidateSubCampus/${campusCode}/${sub}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setDegreeCodes(res.data.degrees || []))
      .catch(console.error);
  };

  const fetchBranches = (deg) => {
    axios
      .get(`${API_URL}/api/UpdateClassesValidateDegree/${campusCode}/${deg}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setBranchCodes(res.data.branches || []))
      .catch(console.error);
  };

  // ✅ UPDATED: subjects are now pulled from codedegreesubjectmap (map-driven)
  // Backend expected:
  // GET /api/UpdateClassesSubjectsMap/:campusCode
  //   params: SubCampusCode, DegreeCode, BranchCode, Regulation, Semester
  const fetchSubjectsFromMap = async ({ SubCampusCode, DegreeCode, BranchCode, Regulation, Semester }) => {
    if (!SubCampusCode || !DegreeCode || !BranchCode || !Regulation || !Semester) {
      setSubjects([]);
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/UpdateClassesSubjectsMap/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { SubCampusCode, DegreeCode, BranchCode, Regulation, Semester },
      });
      setSubjects(res.data?.subjects || []);
    } catch (e) {
      console.error(e);
      setSubjects([]);
    }
  };

  const downloadSelectedClasses = () => {
    const selected = rows.filter((r) => selectedRows.includes(r.ID));
  
    if (!selected.length) {
      setToast({
        open: true,
        type: "warning",
        msg: "Select at least one class to download.",
      });
      return;
    }
  
    const headers = [
      "AcademicYear",
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "Regulation",
      "Semester",
      "SubjectCode",
      "SubjectNameCode",
      "SubjectName",
      "Section",
      "RoomNumber",
      "TeacherID",
      "TeacherName",
      "ClassRepresentativeID",
      "ClassRepresentativeName",
      "IsActive",
    ];
  
    const exportRows = selected.map((r) => {
      const obj = {};
      headers.forEach((h) => {
        obj[h] = r[h] ?? "";
      });
      return obj;
    });
  
    const ws = XLSX.utils.json_to_sheet(exportRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(16, h.length + 2) }));
  
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SelectedClasses");
  
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  
    saveAs(
      new Blob([out], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Selected_Classes_${campusCode}.xlsx`
    );
  };

  const handleBulkCreateClasses = async () => {
    const f = bulkCreateForm;
  
    if (
      !f.AcademicYear ||
      !f.SubCampusCode.length ||
      !f.DegreeCode.length ||
      !f.BranchCode.length ||
      !f.Regulation.length ||
      !f.Semester.length ||
      !f.SubjectCode.length ||
      !f.Section.length
    ) {
      setToast({
        open: true,
        type: "warning",
        msg: "Select academic year, scope, subjects, and sections.",
      });
      return;
    }
  
    try {
      setBulkCreating(true);
      setBulkCreateReport(null);
  
      const { data } = await axios.post(
        `${API_URL}/api/UpdateClasses/bulk-create`,
        normalizeClassPayload(f),
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      setBulkCreateReport(data);
      setToast({
        open: true,
        type: data.skipped ? "warning" : "success",
        msg: `Created ${data.inserted} class(es). Skipped ${data.skipped}.`,
      });
  
      await fetchData();
      setFilters((p) => ({ ...p, AcademicYear: [f.AcademicYear] }));
      setFiltersApplied(true);
    } catch (e) {
      const msg = e.response?.data?.error || "Bulk create failed.";
      setBulkCreateReport({ error: msg });
      setToast({ open: true, type: "error", msg });
    } finally {
      setBulkCreating(false);
    }
  };

  // ─── new-row handlers ──────────────────────────────────────────────────
  const refreshSubjectsIfReady = (draftRow) => {
    fetchSubjectsFromMap({
      SubCampusCode: draftRow.SubCampusCode,
      DegreeCode: draftRow.DegreeCode,
      BranchCode: draftRow.BranchCode,
      Regulation: draftRow.Regulation,
      Semester: draftRow.Semester,
    });
  };

  const handleNewRowChange = (field, value) => {
    setNewRow((r) => {
      const next = { ...r, [field]: value };

      // when these change, clear subject selection (because map scope changes)
      if (["SubCampusCode", "DegreeCode", "BranchCode", "Regulation", "Semester"].includes(field)) {
        next.SubjectCode = "";
        next.SubjectNameCode = "";
        next.SubjectName = "";
      }

      return next;
    });

    if (field === "SubCampusCode") {
      fetchDegrees(value);
      setNewRow((r) => ({ ...r, TeacherID: "", ClassRepresentativeID: "" }));
      fetchTeachersForSub(value);
      fetchClassRepresentativesForSub(value);
    }
    if (field === "DegreeCode") fetchBranches(value);

    // ✅ refresh subjects from map once we have all keys
    if (["SubCampusCode", "DegreeCode", "BranchCode", "Regulation", "Semester"].includes(field)) {
      const draft = { ...newRow, [field]: value };
      refreshSubjectsIfReady(draft);
    }
  };

  const handleSubjectChange = (field, value) => {
    const sub = subjects.find((s) => s[field] === value);
    if (sub) {
      setNewRow((r) => ({
        ...r,
        SubjectCode: sub.SubjectCode,
        SubjectNameCode: sub.SubjectNameCode,
        SubjectName: sub.SubjectName,
        // MapID: sub.MapID, // if backend returns MapID per subject row, you may store it
      }));
    }
  };

  const handleNewRowActiveToggle = (e) => {
    setNewRow((r) => ({ ...r, IsActive: e.target.checked ? 1 : 0 }));
  };

  const handleInputChange = (e, id = null) => {
    const { name, value, checked } = e.target;
    let v = value;
    if (name === "IsActive") v = checked ? 1 : 0;
    if (name === "TeacherID" || name === "ClassRepresentativeID") v = String(value);

    if (id == null) setNewRow((r) => ({ ...r, [name]: v }));
    else setRows((r) => r.map((x) => (x.ID === id ? { ...x, [name]: v } : x)));
  };

  useEffect(() => {
    if (!editRowId) return;
    const row = rows.find((r) => r.ID === editRowId);
    const key = (row?.SubCampusCode ?? "").trim();
    if (key) {
      fetchTeachersForSub(key);
      fetchClassRepresentativesForSub(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRowId]);

  const classSectionOptions = useMemo(() =>
    uniqueSorted([
      ...(sectionOptionsFromTable || []),
      ...(options.Section || []),
      ...(rows || []).map((r) => r.Section),
    ]),
    [sectionOptionsFromTable, options.Section, rows]
  );

  const currentTeacherOptions = useMemo(() => {
    const key = String(newRow.SubCampusCode || "").trim();
    return (teachersBySub[key] || []).map((t) => ({
      ...t,
      value: String(t.TeacherID ?? ""),
      label: `${t.TeacherName || "Teacher"} (${t.EmployeeID || t.TeacherID || ""})`,
    }));
  }, [newRow.SubCampusCode, teachersBySub]);

  const currentClassRepOptions = useMemo(() => {
    const key = String(newRow.SubCampusCode || "").trim();
    return (classrepresentativesBySub[key] || []).map((r) => ({
      ...r,
      value: String(r.ClassRepresentativeID ?? ""),
      label: `${r.ClassRepresentativeName || "Class Representative"} (${r.StudentID || r.StudentKey || r.ClassRepresentativeID || ""})`,
    }));
  }, [newRow.SubCampusCode, classrepresentativesBySub]);

  const resetClassForm = () => {
    setNewRow({
      AcademicYear: "", SubCampusCode: "", DegreeCode: "", BranchCode: "", Regulation: "",
      Semester: "", SubjectCode: "", SubjectNameCode: "", SubjectName: "", Section: "",
      RoomNumber: "", TeacherID: "", ClassRepresentativeID: "", IsActive: 0,
    });
    setSubjects([]);
  };

  const openAddClassDialog = () => {
    setClassDialogMode("add");
    setClassDialogEditId(null);
    resetClassForm();
    setError("");
    setClassDialogOpen(true);
  };

  // ───  Add  ─────────────────────────────────────────────────────────────
  const handleAddRow = async () => {
    const prev = rows;
    try {
      await axios.post(
        `${API_URL}/api/UpdateClasses`,
        normalizeClassPayload(newRow),
        //{ ...newRow },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(true);
      await fetchData();
      setNewRow({
        AcademicYear: "",
        SubCampusCode: "",
        DegreeCode: "",
        BranchCode: "",
        Regulation: "",
        Semester: "",
        SubjectCode: "",
        SubjectNameCode: "",
        SubjectName: "",
        Section: "",
        RoomNumber: "",
        TeacherID: "",
        ClassRepresentativeID: "",
        IsActive: 0,
      });
      setSubjects([]);
      setError("");
      setClassDialogOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add class");
      setRows(prev);
    }
  };

  const handleStartEdit = (row) => {
    setClassDialogMode("edit");
    setClassDialogEditId(row.ID);
    setNewRow({
      AcademicYear: row.AcademicYear ?? "",
      SubCampusCode: row.SubCampusCode ?? "",
      DegreeCode: row.DegreeCode ?? "",
      BranchCode: row.BranchCode ?? "",
      Regulation: row.Regulation ?? "",
      Semester: row.Semester ?? "",
      SubjectCode: row.SubjectCode ?? "",
      SubjectNameCode: row.SubjectNameCode ?? "",
      SubjectName: row.SubjectName ?? "",
      Section: row.Section ?? "",
      RoomNumber: row.RoomNumber ?? "",
      TeacherID: row.TeacherID ?? "",
      ClassRepresentativeID: row.ClassRepresentativeID ?? "",
      IsActive: Number(row.IsActive ?? 0),
    });
    setError("");
    if (row.SubCampusCode) {
      fetchDegrees(row.SubCampusCode);
      fetchTeachersForSub(row.SubCampusCode);
      fetchClassRepresentativesForSub(row.SubCampusCode);
    }
    if (row.DegreeCode) fetchBranches(row.DegreeCode);
    fetchSubjectsFromMap({
      SubCampusCode: row.SubCampusCode, DegreeCode: row.DegreeCode, BranchCode: row.BranchCode,
      Regulation: row.Regulation, Semester: row.Semester,
    });
    setClassDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setClassDialogOpen(false);
    setClassDialogEditId(null);
    setEditOriginalRow(null);
    setEditRowId(null);
    setError("");
  };

  // ─── Save / Update ─────────────────────────────────────────────────────
  const handleSaveRow = async (id) => {
    try {
      setClassSaving(true);
      await axios.put(`${API_URL}/api/UpdateClasses/${id}`, normalizeClassPayload(newRow), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      await fetchData();
      setError("");
      setClassDialogOpen(false);
      setClassDialogEditId(null);
      setEditRowId(null);
      setEditOriginalRow(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save changes");
    } finally {
      setClassSaving(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────
  const handleDeleteRow = async (id) => {
    const prev = rows;
    try {
      await axios.delete(`${API_URL}/api/UpdateClasses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      await fetchData();
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete class");
      setRows(prev);
    }
  };

  // ─── Bulk upload ───────────────────────────────────────────────────────
  const handleBulkClick = () => fileInputRef.current.click();
  const handleFile = async (e) => {
    const prev = rows;
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    try {
      await axios.post(`${API_URL}/api/UploadClasses`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      await fetchData();
      setSuccess(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
      setRows(prev);
    } finally {
      e.target.value = "";
    }
  };

  const teacherNameFor = (subCampusCode, teacherId) => {
    const key = (subCampusCode ?? "").trim();
    const list = teachersBySub[key] || [];
    return list.find((t) => String(t.TeacherID) === String(teacherId))
      ?.TeacherName || "";
  };

  const classrepresentativeNameFor = (subCampusCode, classrepresentativeId) => {
    const key = (subCampusCode ?? "").trim();
    const list = classrepresentativesBySub[key] || [];
    return (
      list.find((t) => String(t.ClassRepresentativeID) === String(classrepresentativeId))
        ?.ClassRepresentativeName || ""
    );
  };

  // --- helpers: normalize + match ---
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const inFilter = (selected = [], value) =>
    !selected?.length || selected.some((s) => norm(s) === norm(value));

  const filterRowsForOptions = (field) => {
    const order = [
      "AcademicYear",
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "Regulation",
      "SemesterCode",
      "Section",
      "SubjectCode",
      "SubjectNameCode",
      "TeacherName",
      "ClassRepresentativeName",
      "IsActive",
    ];

    const idx = order.indexOf(field);
    const previousFields = idx <= 0 ? [] : order.slice(0, idx);

    return (rows || []).filter((r) =>
      previousFields.every((f) => {
        const selected = filters[f] || [];
        if (!selected.length) return true;

        let rowValue = "";

        if (f === "SemesterCode") {
          rowValue = String(r.Semester ?? "");
        } else if (f === "TeacherName") {
          rowValue =
            r.TeacherName ||
            teacherNameFor(r.SubCampusCode, r.TeacherID);
        } else if (f === "ClassRepresentativeName") {
          rowValue =
            r.ClassRepresentativeName ||
            classrepresentativeNameFor(
              r.SubCampusCode,
              r.ClassRepresentativeID
            );
        } else if (f === "IsActive") {
          rowValue =
            Number(r.IsActive) === 1
              ? "Active"
              : "Inactive";
        } else {
          rowValue = r[f];
        }

        return selected.some((v) => norm(v) === norm(rowValue));
      })
    );
  };

  function uniqueSorted(arr = []) {
    return Array.from(
      new Set(
        arr
          .map((x) => String(x ?? "").trim())
          .filter(Boolean)
      )
    ).sort();
  }

  const manageFilterOptions = useMemo(() => {
    return {
      AcademicYear: uniqueSorted(
        rows.map((r) => r.AcademicYear)
      ),
  
      SubCampusCode: uniqueSorted(
        filterRowsForOptions("SubCampusCode").map(
          (r) => r.SubCampusCode
        )
      ),
  
      DegreeCode: uniqueSorted(
        filterRowsForOptions("DegreeCode").map(
          (r) => r.DegreeCode
        )
      ),
  
      BranchCode: uniqueSorted(
        filterRowsForOptions("BranchCode").map(
          (r) => r.BranchCode
        )
      ),
  
      Regulation: uniqueSorted(
        filterRowsForOptions("Regulation").map(
          (r) => r.Regulation
        )
      ),
  
      SemesterCode: uniqueSorted(
        filterRowsForOptions("SemesterCode").map(
          (r) => String(r.Semester)
        )
      ),
  
      Section: uniqueSorted(
        filterRowsForOptions("Section").map(
          (r) => String(r.Section)
        )
      ),
  
      SubjectCode: uniqueSorted(
        filterRowsForOptions("SubjectCode").map(
          (r) => r.SubjectCode
        )
      ),
  
      SubjectNameCode: uniqueSorted(
        filterRowsForOptions("SubjectNameCode").map(
          (r) => r.SubjectNameCode
        )
      ),
  
      TeacherName: uniqueSorted(
        filterRowsForOptions("TeacherName").map(
          (r) =>
            r.TeacherName ||
            teacherNameFor(
              r.SubCampusCode,
              r.TeacherID
            )
        )
      ),
  
      ClassRepresentativeName: uniqueSorted(
        filterRowsForOptions(
          "ClassRepresentativeName"
        ).map(
          (r) =>
            r.ClassRepresentativeName ||
            classrepresentativeNameFor(
              r.SubCampusCode,
              r.ClassRepresentativeID
            )
        )
      ),
  
      IsActive: ["Active", "Inactive"],
    };
  }, [
    rows,
    filters,
    teachersBySub,
    classrepresentativesBySub,
    editRowId,
  ]);
  // ✅ whether at least ONE filter has a selection
  const hasAnyFilterSelected = useMemo(() => {
    return Object.values(filters).some((arr) => Array.isArray(arr) && arr.length > 0);
  }, [filters]);

  // ✅ rows -> filteredRows (ONLY when filtersApplied && hasAnyFilterSelected)
  const filteredRows = useMemo(() => {
    if (!filtersApplied || !hasAnyFilterSelected) return [];

    return (rows || []).filter((r) => {
      // Keep an edited row visible even if an unsaved change makes it stop
      // matching the currently applied filter (e.g. Active -> Inactive).
      if (editRowId != null && r.ID === editRowId) return true;

      const okYear = inFilter(filters.AcademicYear, r.AcademicYear);
      const okSub = inFilter(filters.SubCampusCode, r.SubCampusCode);
      const okDeg = inFilter(filters.DegreeCode, r.DegreeCode);
      const okBr = inFilter(filters.BranchCode, r.BranchCode);
      const okReg = inFilter(filters.Regulation, r.Regulation);
      const okSem = inFilter(filters.SemesterCode, String(r.Semester));
      const okSection = inFilter(filters.Section, String(r.Section));
      const activeValue =
        Number(r.IsActive) === 1
          ? "Active"
          : "Inactive";

      const okActive = inFilter(
        filters.IsActive,
        activeValue
      );

      const okSubjectCode =
        inFilter(filters.SubjectCode, r.SubjectCode);

      const okSubject =
        inFilter(filters.SubjectNameCode, r.SubjectNameCode);

      const teacherName =
        r.TeacherName || teacherNameFor(r.SubCampusCode, r.TeacherID);

      const crName =
        r.ClassRepresentativeName ||
        classrepresentativeNameFor(
          r.SubCampusCode,
          r.ClassRepresentativeID
        );

      const okTeacher =
        inFilter(filters.TeacherName, teacherName);

      const okClassRepresentative =
        inFilter(filters.ClassRepresentativeName, crName);

      return (
        okYear &&
        okSub &&
        okDeg &&
        okBr &&
        okReg &&
        okSem &&
        okSection &&
        okSubjectCode &&
        okSubject &&
        okTeacher &&
        okClassRepresentative &&
        okActive
      );
    });
  }, [
    rows,
    filters,
    filtersApplied,
    hasAnyFilterSelected,
    teachersBySub,
    classrepresentativesBySub,
  ]);

  const paginatedRows = useMemo(() => {
  const start = page * rowsPerPage;
  const end = start + rowsPerPage;

  return filteredRows.slice(start, end);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [
    filtersApplied,
    filters.AcademicYear,
    filters.SubCampusCode,
    filters.DegreeCode,
    filters.BranchCode,
    filters.Regulation,
    filters.SemesterCode,
    filters.Section,
    filters.SubjectCode,
    filters.SubjectNameCode,
    filters.TeacherName,
    filters.ClassRepresentativeName,
    filters.IsActive,
  ]);

  useEffect(() => {
    const maximumPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1
    );

    if (page > maximumPage) {
      setPage(maximumPage);
    }
  }, [filteredRows.length, page, rowsPerPage]);

  // ✅ table selection helpers (based on filteredRows)
  const allSelected =
    paginatedRows.length > 0 &&
    paginatedRows.every((r) =>
      selectedRows.includes(r.ID)
    );

  const somePageRowsSelected =
    paginatedRows.some((r) =>
      selectedRows.includes(r.ID)
    );
  const isRowSelected = (id) => selectedRows.includes(id);

  const handleSelectAll = (e) => {
    const currentPageIds = paginatedRows.map(
      (r) => r.ID
    );
  
    if (e.target.checked) {
      setSelectedRows((previous) => [
        ...new Set([
          ...previous,
          ...currentPageIds,
        ]),
      ]);
    } else {
      setSelectedRows((previous) =>
        previous.filter(
          (id) => !currentPageIds.includes(id)
        )
      );
    }
  };

  const handleSelectRow = (id) => (e) => {
    if (e.target.checked) setSelectedRows((prev) => [...prev, id]);
    else setSelectedRows((prev) => prev.filter((x) => x !== id));
  };

  // ✅ bulk active/inactive
  const handleBulkActiveUpdate = async (makeActive) => {
    if (!selectedRows.length) return;

    try {
      const promises = selectedRows.map((id) => {
        const row = rows.find((r) => r.ID === id);
        if (!row) return Promise.resolve();
        return axios.put(
          `${API_URL}/api/UpdateClasses/${id}`,
          normalizeClassPayload({ ...row, IsActive: makeActive ? 1 : 0 }),
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      await Promise.all(promises);
      setToast({
        open: true,
        type: "success",
        msg: `Updated ${selectedRows.length} row(s)`,
      });
      setSelectedRows([]);
      await fetchData();
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed bulk update." });
    }
  };

  // ✅ Filter / Clear handlers
  const handleApplyFilters = () => {
    if (!hasAnyFilterSelected) {
      setToast({
        open: true,
        type: "warning",
        msg: "Select at least one filter, then click Filter.",
      });

      setFiltersApplied(false);
      setSelectedRows([]);
      setPage(0);
      return;
    }

    setFiltersApplied(true);
    setSelectedRows([]);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      AcademicYear: [],
      SubCampusCode: [],
      DegreeCode: [],
      BranchCode: [],
      Regulation: [],
      SemesterCode: [],
      Section: [],
      SubjectCode: [],
      SubjectNameCode: [],
      TeacherName: [],
      ClassRepresentativeName: [],
      // Clear means no status restriction.
      IsActive: ["Active"],
    });

    setFiltersApplied(false);
    setSelectedRows([]);
    setPage(0);
  };

  const rowFilterValue = (r, field) => {
    if (field === "SemesterCode") return String(r.Semester ?? "");
    if (field === "TeacherName") {
      return r.TeacherName || teacherNameFor(r.SubCampusCode, r.TeacherID);
    }
    if (field === "ClassRepresentativeName") {
      return (
        r.ClassRepresentativeName ||
        classrepresentativeNameFor(r.SubCampusCode, r.ClassRepresentativeID)
      );
    }
    if (field === "IsActive") return Number(r.IsActive) === 1 ? "Active" : "Inactive";
    return r[field];
  };

  const bulkAssignOrder = [
    "AcademicYear",
    "SubCampusCode",
    "DegreeCode",
    "BranchCode",
    "Regulation",
    "SemesterCode",
    "Section",
    "SubjectCode",
    "SubjectNameCode",
    "TeacherName",
    "ClassRepresentativeName",
    "IsActive",
  ];

  const bulkAssignRowsForOptions = (field) => {
    const idx = bulkAssignOrder.indexOf(field);
    const previousFields = idx <= 0 ? [] : bulkAssignOrder.slice(0, idx);

    return (rows || []).filter((r) =>
      previousFields.every((f) =>
        inFilter(bulkAssignFilters[f] || [], rowFilterValue(r, f))
      )
    );
  };

  const bulkAssignFilterOptions = useMemo(() => ({
    AcademicYear: uniqueSorted(rows.map((r) => r.AcademicYear)),
    SubCampusCode: uniqueSorted(bulkAssignRowsForOptions("SubCampusCode").map((r) => r.SubCampusCode)),
    DegreeCode: uniqueSorted(bulkAssignRowsForOptions("DegreeCode").map((r) => r.DegreeCode)),
    BranchCode: uniqueSorted(bulkAssignRowsForOptions("BranchCode").map((r) => r.BranchCode)),
    Regulation: uniqueSorted(bulkAssignRowsForOptions("Regulation").map((r) => r.Regulation)),
    SemesterCode: uniqueSorted(bulkAssignRowsForOptions("SemesterCode").map((r) => String(r.Semester))),
    Section: uniqueSorted(bulkAssignRowsForOptions("Section").map((r) => String(r.Section))),
    SubjectCode: uniqueSorted(bulkAssignRowsForOptions("SubjectCode").map((r) => r.SubjectCode)),
    SubjectNameCode: uniqueSorted(bulkAssignRowsForOptions("SubjectNameCode").map((r) => r.SubjectNameCode)),
    TeacherName: uniqueSorted(bulkAssignRowsForOptions("TeacherName").map((r) => rowFilterValue(r, "TeacherName"))),
    ClassRepresentativeName: uniqueSorted(
      bulkAssignRowsForOptions("ClassRepresentativeName").map((r) => rowFilterValue(r, "ClassRepresentativeName"))
    ),
    IsActive: ["Active", "Inactive"],
  }), [rows, bulkAssignFilters, teachersBySub, classrepresentativesBySub]);

  const hasAnyBulkAssignFilter = useMemo(
    () => Object.values(bulkAssignFilters).some((arr) => Array.isArray(arr) && arr.length > 0),
    [bulkAssignFilters]
  );

  const bulkAssignMatchedRows = useMemo(() => {
    if (!bulkAssignFiltersApplied || !hasAnyBulkAssignFilter) return [];
    return (rows || []).filter((r) =>
      bulkAssignOrder.every((field) =>
        inFilter(bulkAssignFilters[field] || [], rowFilterValue(r, field))
      )
    );
  }, [
    rows,
    bulkAssignFilters,
    bulkAssignFiltersApplied,
    hasAnyBulkAssignFilter,
    teachersBySub,
    classrepresentativesBySub,
  ]);

  const bulkAssignSubCampuses = useMemo(
    () => uniqueSorted(bulkAssignMatchedRows.map((r) => r.SubCampusCode)),
    [bulkAssignMatchedRows]
  );

  // Build one assignee list across every SubCampus represented by the current
  // result set. This intentionally does NOT restrict assignment to one
  // SubCampus: the selected teacher / class rep can be assigned to classes
  // across multiple SubCampuses in the same operation.
  const bulkTeacherOptions = useMemo(() => {
    const byTeacher = new Map();

    bulkAssignSubCampuses.forEach((subCampusCode) => {
      (teachersBySub[subCampusCode] || []).forEach((teacher) => {
        // EmployeeID is returned by the updated backend lookup. Fallback to
        // TeacherID keeps this frontend backward compatible during deployment.
        const assigneeKey = String(teacher.EmployeeID ?? teacher.TeacherID ?? "").trim();
        if (!assigneeKey) return;

        const existing = byTeacher.get(assigneeKey) || {
          ...teacher,
          _AssigneeKey: assigneeKey,
          _SubCampusCodes: [],
        };

        if (!existing._SubCampusCodes.includes(subCampusCode)) {
          existing._SubCampusCodes.push(subCampusCode);
        }
        byTeacher.set(assigneeKey, existing);
      });
    });

    return [...byTeacher.values()].sort((a, b) =>
      String(a.TeacherName || a._AssigneeKey || "").localeCompare(
        String(b.TeacherName || b._AssigneeKey || "")
      )
    );
  }, [bulkAssignSubCampuses, teachersBySub]);

  const bulkClassRepOptions = useMemo(() => {
    const byStudent = new Map();

    bulkAssignSubCampuses.forEach((subCampusCode) => {
      (classrepresentativesBySub[subCampusCode] || []).forEach((rep) => {
        // StudentKey is returned by the updated backend lookup. Fallback to
        // assignment ID keeps the frontend backward compatible during deployment.
        const assigneeKey = String(rep.StudentKey ?? rep.ClassRepresentativeID ?? "").trim();
        if (!assigneeKey) return;

        const existing = byStudent.get(assigneeKey) || {
          ...rep,
          _AssigneeKey: assigneeKey,
          _SubCampusCodes: [],
        };

        if (!existing._SubCampusCodes.includes(subCampusCode)) {
          existing._SubCampusCodes.push(subCampusCode);
        }
        byStudent.set(assigneeKey, existing);
      });
    });

    return [...byStudent.values()].sort((a, b) =>
      String(a.ClassRepresentativeName || a._AssigneeKey || "").localeCompare(
        String(b.ClassRepresentativeName || b._AssigneeKey || "")
      )
    );
  }, [bulkAssignSubCampuses, classrepresentativesBySub]);

  useEffect(() => {
    setBulkAssigneeId("");
  }, [bulkAssignmentType]);

  const bulkAssignPaginatedRows = useMemo(() => {
    const start = bulkAssignPage * bulkAssignRowsPerPage;
    return bulkAssignMatchedRows.slice(start, start + bulkAssignRowsPerPage);
  }, [bulkAssignMatchedRows, bulkAssignPage, bulkAssignRowsPerPage]);

  const bulkAssignAllPageSelected =
    bulkAssignPaginatedRows.length > 0 &&
    bulkAssignPaginatedRows.every((r) => bulkAssignSelectedRows.includes(r.ID));

  const bulkAssignSomePageSelected =
    bulkAssignPaginatedRows.some((r) => bulkAssignSelectedRows.includes(r.ID));

  const handleBulkAssignSelectAllPage = (e) => {
    const pageIds = bulkAssignPaginatedRows.map((r) => r.ID);
    if (e.target.checked) {
      setBulkAssignSelectedRows((prev) => [...new Set([...prev, ...pageIds])]);
    } else {
      setBulkAssignSelectedRows((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  const handleBulkAssignSelectRow = (id) => (e) => {
    if (e.target.checked) {
      setBulkAssignSelectedRows((prev) => [...new Set([...prev, id])]);
    } else {
      setBulkAssignSelectedRows((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleSelectAllBulkAssignMatches = () => {
    setBulkAssignSelectedRows(bulkAssignMatchedRows.map((r) => r.ID));
  };

  const handleClearBulkAssignSelection = () => {
    setBulkAssignSelectedRows([]);
  };

  useEffect(() => {
    const matchedIds = new Set(bulkAssignMatchedRows.map((r) => r.ID));
    setBulkAssignSelectedRows((prev) => prev.filter((id) => matchedIds.has(id)));
    setBulkAssignPage(0);
  }, [bulkAssignMatchedRows]);

  useEffect(() => {
    const maximumPage = Math.max(
      0,
      Math.ceil(bulkAssignMatchedRows.length / bulkAssignRowsPerPage) - 1
    );
    if (bulkAssignPage > maximumPage) setBulkAssignPage(maximumPage);
  }, [bulkAssignMatchedRows.length, bulkAssignPage, bulkAssignRowsPerPage]);

  const handleApplyBulkAssignFilters = () => {
    if (!hasAnyBulkAssignFilter) {
      setToast({ open: true, type: "warning", msg: "Select at least one filter." });
      return;
    }
    setBulkAssignFiltersApplied(true);
    setBulkAssigneeId("");
    setBulkAssignSelectedRows([]);
    setBulkAssignPage(0);
  };

  const handleClearBulkAssignFilters = () => {
    setBulkAssignFilters(emptyBulkAssignFilters);
    setBulkAssignFiltersApplied(false);
    setBulkAssigneeId("");
    setBulkAssignSelectedRows([]);
    setBulkAssignPage(0);
  };

  const handleBulkAssignment = async () => {
    if (!bulkAssignSelectedRows.length) {
      setToast({ open: true, type: "warning", msg: "Select at least one class to assign." });
      return;
    }
    if (!bulkAssigneeId) {
      setToast({ open: true, type: "warning", msg: "Select a teacher or class representative." });
      return;
    }

    try {
      setBulkAssigning(true);
      const { data } = await axios.post(
        `${API_URL}/api/UpdateClasses/bulk-assign`,
        {
          ClassIDs: bulkAssignSelectedRows,
          AssignmentType: bulkAssignmentType,
          AssigneeKey: bulkAssigneeId,
        },
        authHeader
      );
      setToast({
        open: true,
        type: "success",
        msg: data?.message || `Updated ${bulkAssignSelectedRows.length} class(es).`,
      });
      await fetchData();
      setBulkAssigneeId("");
      setBulkAssignSelectedRows([]);
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        msg: e.response?.data?.error || "Bulk assignment failed.",
      });
    } finally {
      setBulkAssigning(false);
    }
  };

  const bulkDeleteRowsForOptions = (field) => {
    const idx = bulkAssignOrder.indexOf(field);
    const previousFields = idx <= 0 ? [] : bulkAssignOrder.slice(0, idx);
    return (rows || []).filter((r) =>
      previousFields.every((f) =>
        inFilter(bulkDeleteFilters[f] || [], rowFilterValue(r, f))
      )
    );
  };

  const bulkDeleteFilterOptions = useMemo(() => ({
    AcademicYear: uniqueSorted(rows.map((r) => r.AcademicYear)),
    SubCampusCode: uniqueSorted(bulkDeleteRowsForOptions("SubCampusCode").map((r) => r.SubCampusCode)),
    DegreeCode: uniqueSorted(bulkDeleteRowsForOptions("DegreeCode").map((r) => r.DegreeCode)),
    BranchCode: uniqueSorted(bulkDeleteRowsForOptions("BranchCode").map((r) => r.BranchCode)),
    Regulation: uniqueSorted(bulkDeleteRowsForOptions("Regulation").map((r) => r.Regulation)),
    SemesterCode: uniqueSorted(bulkDeleteRowsForOptions("SemesterCode").map((r) => String(r.Semester))),
    Section: uniqueSorted(bulkDeleteRowsForOptions("Section").map((r) => String(r.Section))),
    SubjectCode: uniqueSorted(bulkDeleteRowsForOptions("SubjectCode").map((r) => r.SubjectCode)),
    SubjectNameCode: uniqueSorted(bulkDeleteRowsForOptions("SubjectNameCode").map((r) => r.SubjectNameCode)),
    TeacherName: uniqueSorted(bulkDeleteRowsForOptions("TeacherName").map((r) => rowFilterValue(r, "TeacherName"))),
    ClassRepresentativeName: uniqueSorted(bulkDeleteRowsForOptions("ClassRepresentativeName").map((r) => rowFilterValue(r, "ClassRepresentativeName"))),
    IsActive: ["Active", "Inactive"],
  }), [rows, bulkDeleteFilters, teachersBySub, classrepresentativesBySub]);

  const hasAnyBulkDeleteFilter = useMemo(
    () => Object.values(bulkDeleteFilters).some((arr) => Array.isArray(arr) && arr.length > 0),
    [bulkDeleteFilters]
  );

  const bulkDeleteMatchedRows = useMemo(() => {
    if (!bulkDeleteFiltersApplied || !hasAnyBulkDeleteFilter) return [];
    return (rows || []).filter((r) =>
      bulkAssignOrder.every((field) =>
        inFilter(bulkDeleteFilters[field] || [], rowFilterValue(r, field))
      )
    );
  }, [rows, bulkDeleteFilters, bulkDeleteFiltersApplied, hasAnyBulkDeleteFilter, teachersBySub, classrepresentativesBySub]);

  const paginatedBulkDeleteRows = useMemo(() => {
    const start = bulkDeletePage * bulkDeleteRowsPerPage;
    return bulkDeleteMatchedRows.slice(start, start + bulkDeleteRowsPerPage);
  }, [bulkDeleteMatchedRows, bulkDeletePage, bulkDeleteRowsPerPage]);

  useEffect(() => {
    const ids = new Set(bulkDeleteMatchedRows.map((r) => r.ID));
    setBulkDeleteSelectedRows((prev) => prev.filter((id) => ids.has(id)));
    setBulkDeletePage(0);
  }, [bulkDeleteMatchedRows]);

  const handleBulkDeleteClasses = async () => {
    if (!bulkDeleteSelectedRows.length) {
      setToast({ open: true, type: "warning", msg: "Select at least one class to delete." });
      return;
    }
    if (!window.confirm(`Delete ${bulkDeleteSelectedRows.length} selected class(es)? This cannot be undone.`)) return;
    try {
      setBulkDeleting(true);
      setBulkDeleteReport(null);
      const { data } = await axios.post(
        `${API_URL}/api/UpdateClasses/bulk-delete`,
        { ClassIDs: bulkDeleteSelectedRows },
        authHeader
      );
      setBulkDeleteReport(data);
      setToast({ open: true, type: data?.skipped ? "warning" : "success", msg: data?.message || `Deleted ${data?.deleted || 0} class(es).` });
      setBulkDeleteSelectedRows([]);
      await fetchData();
    } catch (e) {
      const msg = e.response?.data?.error || "Bulk delete failed.";
      setBulkDeleteReport({ error: msg });
      setToast({ open: true, type: "error", msg });
    } finally {
      setBulkDeleting(false);
    }
  };

  const copySourceRows = useMemo(() => {
    if (!copyForm.FromAcademicYear) return [];

    // Historical classes remain valid copy templates even after they are
    // deactivated. Destination status is controlled by ActivateNew.
    return rows.filter(
      (r) =>
        String(r.AcademicYear ?? "").trim() ===
        String(copyForm.FromAcademicYear ?? "").trim()
    );
  }, [rows, copyForm.FromAcademicYear]);

  const copyRowsForOptions = (field) => {
    const order = [
      "SubCampusCode",
      "DegreeCode",
      "BranchCode",
      "Regulation",
      "Semester",
      "SubjectCode",
      "Section",
    ];

    const idx = order.indexOf(field);
    const previousFields = idx <= 0 ? [] : order.slice(0, idx);

    return copySourceRows.filter((r) =>
      previousFields.every((f) => {
        const selected = copyForm[f] || [];
        if (!selected.length) return true;
        return selected.some((v) => norm(v) === norm(r[f]));
      })
    );
  };

  const copyOptions = useMemo(() => {
    return {
      SubCampusCode: uniqueSorted(copyRowsForOptions("SubCampusCode").map((r) => r.SubCampusCode)),
      DegreeCode: uniqueSorted(copyRowsForOptions("DegreeCode").map((r) => r.DegreeCode)),
      BranchCode: uniqueSorted(copyRowsForOptions("BranchCode").map((r) => r.BranchCode)),
      Regulation: uniqueSorted(copyRowsForOptions("Regulation").map((r) => r.Regulation)),
      Semester: uniqueSorted(copyRowsForOptions("Semester").map((r) => String(r.Semester))),
      Section: uniqueSorted(copyRowsForOptions("Section").map((r) => String(r.Section))),
    };
  }, [copySourceRows, copyForm]);

  const copySubjectOptions = useMemo(() => {
    return copyRowsForOptions("SubjectCode").map((r) => ({
      SubjectCode: String(r.SubjectCode ?? ""),
      SubjectNameCode: r.SubjectNameCode || "",
      SubjectName: r.SubjectName || "",
    }))
    .filter((x, idx, arr) =>
      x.SubjectCode &&
      arr.findIndex((y) => y.SubjectCode === x.SubjectCode) === idx
    );
  }, [copySourceRows, copyForm]);

  const handleCopyMultiSelectAll = (field, list) => (_, value) => {
    const hasSelectAll = value.some((x) => getOptionValue(x) === SELECT_ALL_VALUE);

    setCopyForm((prev) => ({
      ...prev,
      [field]: hasSelectAll
        ? list.map((x) => getOptionValue(x))
        : value.map(getOptionValue),
    }));
  };

  const clearCopyDependents = (field) => {
    const clearMap = {
      FromAcademicYear: ["SubCampusCode", "DegreeCode", "BranchCode", "Regulation", "Semester", "SubjectCode", "Section"],
      SubCampusCode: ["DegreeCode", "BranchCode", "Regulation", "Semester", "SubjectCode", "Section"],
      DegreeCode: ["BranchCode", "Regulation", "Semester", "SubjectCode", "Section"],
      BranchCode: ["Regulation", "Semester", "SubjectCode", "Section"],
      Regulation: ["Semester", "SubjectCode", "Section"],
      Semester: ["SubjectCode", "Section"],
      SubjectCode: ["Section"],
    };

    return clearMap[field] || [];
  };

  const handleCopyAcademicYear = async () => {
    if (!copyForm.FromAcademicYear || !copyForm.ToAcademicYear) {
      setToast({
        open: true,
        type: "warning",
        msg: "From and To Academic Year are required.",
      });
      return;
    }

    try {
      setCopying(true);
      setCopyReport(null);

      const { data } = await axios.post(
        `${API_URL}/api/classes/copy-academic-year`,
        copyForm,
        authHeader
      );

      setCopyReport(data);
      setToast({
        open: true,
        type: data.skipped ? "warning" : "success",
        msg: `Created ${data.inserted} class(es). Skipped ${data.skipped}.`,
      });

      await fetchData();

      setFilters((p) => ({
        ...p,
        AcademicYear: [copyForm.ToAcademicYear],
      }));

      setFiltersApplied(true);
      setSelectedRows([]);
    } catch (e) {
      const msg = e.response?.data?.error || "Failed to copy classes.";
      setCopyReport({ error: msg });
      setToast({ open: true, type: "error", msg });
    } finally {
      setCopying(false);
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Classes
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Manage class mappings, teachers, class reps, and bulk upload
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {tab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={openAddClassDialog}
                sx={{ ...primaryBtnSx, backgroundColor: "white", color: THEME_ORANGE, "&:hover": { backgroundColor: "rgba(255,255,255,.92)" } }}
              >
                Add New Class
              </Button>
            )}
            <Button
              variant="contained"
              sx={primaryBtnSx}
              disabled={selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(true)}
            >
              Set Bulk Active
            </Button>
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(false)}
            >
              Set Bulk Inactive
            </Button>

            <Divider flexItem orientation="vertical" sx={{ borderColor: "rgba(255,255,255,0.35)" }} />

            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              startIcon={<DownloadIcon />}
              disabled={selectedRows.length === 0}
              onClick={downloadSelectedClasses}
            >
              Download Selected
            </Button>

            <Button
              variant="contained"
              sx={primaryBtnSx}
              startIcon={<UploadIcon />}
              onClick={handleBulkClick}
            >
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Paper elevation={0} sx={{ ...surfaceSx, m: 2, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              borderBottom: `1px solid ${BORDER}`,
              backgroundColor: "#fff",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 900,
                minHeight: 52,
              },
              "& .Mui-selected": {
                color: THEME_ORANGE,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: THEME_ORANGE,
                height: 3,
                borderRadius: 2,
              },
            }}
          >
            <Tab label="Manage Classes" />
            <Tab
              label="Copy Academic Year"
              icon={<ContentCopyIcon />}
              iconPosition="start"
            />
            <Tab
              label="Bulk Assign Teachers / Class Reps"
              icon={<EditIcon />}
              iconPosition="start"
            />
            <Tab
              label="Bulk Create Classes"
              icon={<AddCircleIcon />}
              iconPosition="start"
            />
            <Tab
              label="Bulk Delete Classes"
              icon={<DeleteIcon />}
              iconPosition="start"
            />
          </Tabs>

        <Box sx={{ p: 2, backgroundColor: BG }}>

        {tab === 0 && (
        <>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {!!toast?.open && (
            <Snackbar
              open
              autoHideDuration={3500}
              onClose={() => setToast((t) => ({ ...t, open: false }))}
            >
              <Alert severity={toast.type || "success"} sx={{ borderRadius: 2 }}>
                {toast.msg}
              </Alert>
            </Snackbar>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Filters section */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Filters
                </Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    startIcon={<FilterAltIcon />}
                    onClick={handleApplyFilters}
                    disabled={!hasAnyFilterSelected}
                    title={!hasAnyFilterSelected ? "Select at least one filter" : "Apply filters"}
                  >
                    Filter
                  </Button>
                  <Button
                    variant="outlined"
                    sx={dangerOutlinedBtnSx}
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(6, 1fr)" },
                  gap: 1,
                }}
              >
                {[
                  ["AcademicYear", "Academic Year", manageFilterOptions.AcademicYear],
                  ["SubCampusCode", "SubCampus", manageFilterOptions.SubCampusCode],
                  ["DegreeCode", "Degree", manageFilterOptions.DegreeCode],
                  ["BranchCode", "Branch", manageFilterOptions.BranchCode],
                  ["Regulation", "Regulation", manageFilterOptions.Regulation],
                  ["SemesterCode", "Semester", manageFilterOptions.SemesterCode],
                  ["Section", "Section", manageFilterOptions.Section],
                  ["SubjectCode", "Subject Code", manageFilterOptions.SubjectCode],
                  ["SubjectNameCode", "Subject Name Code", manageFilterOptions.SubjectNameCode],
                  ["TeacherName", "Teacher", manageFilterOptions.TeacherName],
                  ["ClassRepresentativeName", "Class Rep", manageFilterOptions.ClassRepresentativeName],
                  ["IsActive", "Status", manageFilterOptions.IsActive, ],
                ].map(([field, label, list]) => {
                  const opts = withSelectAll(list || []);
                  const selected = opts.filter((x) =>
                    filters[field]?.includes(getOptionValue(x))
                  );
                
                  return (
                    <Autocomplete
                      key={field}
                      multiple
                      options={opts}
                      value={selected}
                      disableCloseOnSelect
                      getOptionLabel={getOptionLabelText}
                      isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
                      onChange={(_, value) => {
                        const hasSelectAll = value.some(
                          (x) => getOptionValue(x) === SELECT_ALL_VALUE
                        );
                      
                        setFilters((prev) => ({
                          ...prev,
                          [field]: hasSelectAll
                            ? list.map((x) => getOptionValue(x))
                            : value.map(getOptionValue),
                        }));
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={
                              option.isSelectAll
                                ? filters[field]?.length === list.length && list.length > 0
                                : selected
                            }
                            sx={checkboxSx}
                          />
                          {getOptionLabelText(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${label}(s)`}
                          size="small"
                          sx={inputSx}
                          helperText={!list.length ? "No matching existing classes." : ""}
                        />
                      )}
                    />
                  );
                })}
              </Box>

              {!filtersApplied && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(25,118,210,0.20)",
                    backgroundColor: "rgba(25,118,210,0.04)",
                  }}
                >
                  Select at least one filter and click <b>Filter</b> to show results.
                </Alert>
              )}
            </Box>
          </Paper>

          {/* OUTER – keeps rounded corners & spacing */}
          <Paper elevation={0} sx={tableWrapSx}>

            {/* INNER – ONLY for horizontal scroll */}
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
          {/* ✅ Table (shows empty until Filter applied) */}
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: 50 }} />  {/* # */}
                <col style={{ width: 40 }} />  {/* select */}
                <col style={{ width: 90 }} />  {/* Sub */}
                <col style={{ width: 90 }} />  {/* Degree */}
                <col style={{ width: 90 }} />  {/* Branch */}
                <col style={{ width: 85 }} />  {/* Regulation */}
                <col style={{ width: 80 }} />  {/* Semester */}
                <col style={{ width: 90 }} />  {/* Subject */}
                <col style={{ width: 110 }} /> {/* NameCode */}
                <col style={{ width: 220 }} /> {/* SubjectName */}
                <col style={{ width: 70 }} />  {/* Section */}
                <col style={{ width: 120 }} /> {/* Room */}
                <col style={{ width: 160 }} /> {/* Teacher */}
                <col style={{ width: 160 }} /> {/* CR */}
                <col style={{ width: 70 }} />  {/* Active */}
                <col style={{ width: 120 }} /> {/* Actions */}
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>

                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={
                        somePageRowsSelected && !allSelected
                      }
                      onChange={handleSelectAll}
                      disabled={paginatedRows.length === 0}
                      sx={{
                        ...checkboxSx,
                        color: "rgba(255,255,255,0.95)",
                        "&.Mui-checked": {
                          color: "#fff",
                        },
                        "&.MuiCheckbox-indeterminate": {
                          color: "#fff",
                        },
                      }}
                    />
                  </TableCell>

                  {[
                    "AcademicYear",
                    "SubCampus",
                    "Degree",
                    "Branch",
                    "Regulation", // ✅ NEW
                    "Semester",
                    "Subject",
                    "NameCode",
                    "SubjectName",
                    "Section",
                    "RoomNumber",
                    "Teacher",
                    "ClassRepresentative",
                    "Active",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.map((row, idx) =>
                  editRowId === row.ID ? (
                    <TableRow key={row.ID} sx={zebraRowSx(idx)}>
                      {/*<TableCell>{idx + 1}</TableCell>*/}
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isRowSelected(row.ID)}
                          onChange={handleSelectRow(row.ID)}
                          sx={checkboxSx}
                        />
                      </TableCell>

                      {[
                        "AcademicYear",
                        "SubCampusCode",
                        "DegreeCode",
                        "BranchCode",
                        "Regulation",
                        "Semester",
                        "SubjectCode",
                        "SubjectNameCode",
                        "SubjectName",
                      ].map((f) => (
                        <TableCell key={f}>{row[f]}</TableCell>
                      ))}

                      <TableCell>
                        <TextField
                          name="Section"
                          value={row.Section}
                          onChange={(e) => handleInputChange(e, row.ID)}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          name="RoomNumber"
                          value={row.RoomNumber}
                          onChange={(e) => handleInputChange(e, row.ID)}
                          fullWidth
                          size="small"
                          sx={inputSx}
                        />
                      </TableCell>
                      {/*
                      <TableCell>
                        <Select
                          name="TeacherID"
                          value={row.TeacherID}
                          onChange={(e) => handleInputChange(e, row.ID)}
                          fullWidth
                          size="small"
                          disabled={!row.SubCampusCode}
                          sx={inputSx}
                        >
                          {(teachersBySub[row.SubCampusCode] || []).map((t) => (
                            <MenuItem key={t.TeacherID} value={t.TeacherID}>
                              {t.TeacherName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                        */}
                      <TableCell>
                        <Select
                          name="TeacherID"
                          value={row.TeacherID ?? ""}         // important
                          onChange={(e) => handleInputChange(e, row.ID)}
                          fullWidth
                          size="small"
                          disabled={!row.SubCampusCode}
                          sx={inputSx}
                          displayEmpty
                          renderValue={(val) => {
                            if (!val) return "None (NULL)";
                            const hit = (teachersBySub[row.SubCampusCode] || []).find(
                              (t) => String(t.TeacherID) === String(val)
                            );
                            return hit?.TeacherName || String(val);
                          }}
                        >
                          {/* ✅ NULL option */}
                          <MenuItem value="">
                            <em>None (NULL)</em>
                          </MenuItem>
                        
                          {(teachersBySub[row.SubCampusCode] || []).map((t) => (
                            <MenuItem key={t.TeacherID} value={String(t.TeacherID)}>
                              {t.TeacherName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      {/*
                      <TableCell>
                        <Select
                          name="ClassRepresentativeID"
                          value={String(row.ClassRepresentativeID ?? "")}
                          onChange={(e) => handleInputChange(e, row.ID)}
                          fullWidth
                          size="small"
                          disabled={!row.SubCampusCode}
                          displayEmpty
                          sx={inputSx}
                          renderValue={(val) => {
                            const key = (row.SubCampusCode ?? "").trim();
                            const opts = classrepresentativesBySub[key] || [];
                            const hit = opts.find((t) => t.ClassRepresentativeID === String(val));
                            return hit?.ClassRepresentativeName || row.ClassRepresentativeName || "";
                          }}
                        >
                          {(classrepresentativesBySub[(row.SubCampusCode ?? "").trim()] || []).map((t) => (
                            <MenuItem key={t.ClassRepresentativeID} value={t.ClassRepresentativeID}>
                              {t.ClassRepresentativeName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      */}
                      <TableCell>
                        <Select
                          name="ClassRepresentativeID"
                          value={String(row.ClassRepresentativeID ?? "")}
                          onChange={(e) => handleInputChange(e, row.ID)}
                          fullWidth
                          size="small"
                          disabled={!row.SubCampusCode}
                          displayEmpty
                          sx={inputSx}
                          renderValue={(val) => {
                            if (!val) return "None (NULL)";
                            const key = (row.SubCampusCode ?? "").trim();
                            const opts = classrepresentativesBySub[key] || [];
                            const hit = opts.find((t) => t.ClassRepresentativeID === String(val));
                            return hit?.ClassRepresentativeName || String(val);
                          }}
                        >
                          {/* ✅ NULL option */}
                          <MenuItem value="">
                            <em>None (NULL)</em>
                          </MenuItem>
                        
                          {(classrepresentativesBySub[(row.SubCampusCode ?? "").trim()] || []).map((t) => (
                            <MenuItem key={t.ClassRepresentativeID} value={String(t.ClassRepresentativeID)}>
                              {t.ClassRepresentativeName}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>


                      <TableCell>
                        <Checkbox
                          name="IsActive"
                          checked={!!row.IsActive}
                          onChange={(e) => handleInputChange(e, row.ID)}
                          sx={checkboxSx}
                        />
                      </TableCell>

                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => handleSaveRow(row.ID)} title="Save">
                          <SaveIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={handleCancelEdit} title="Cancel">
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.ID} sx={zebraRowSx(idx)}>
                      <TableCell>{idx + 1}</TableCell>

                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isRowSelected(row.ID)}
                          onChange={handleSelectRow(row.ID)}
                          sx={checkboxSx}
                        />
                      </TableCell>

                      {[
                        "AcademicYear",
                        "SubCampusCode",
                        "DegreeCode",
                        "BranchCode",
                        "Regulation",
                        "Semester",
                        "SubjectCode",
                        "SubjectNameCode",
                        "SubjectName",
                        "Section",
                        "RoomNumber",
                      ].map((f) => (
                        <TableCell key={f}>{row[f]}</TableCell>
                      ))}

                      <TableCell>{row.TeacherName || teacherNameFor(row.SubCampusCode, row.TeacherID)}</TableCell>
                      <TableCell>
                        {row.ClassRepresentativeName ||
                          classrepresentativeNameFor(row.SubCampusCode, row.ClassRepresentativeID)}
                      </TableCell>
                      <TableCell>
                        <Checkbox checked={!!row.IsActive} readOnly sx={checkboxSx} />
                      </TableCell>
                      <TableCell>
                        <IconButton sx={iconBtnSx} onClick={() => handleStartEdit(row)} title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.ID)} title="Delete">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {/* Add row action - opens popup instead of inline editing */}
                <TableRow>
                  <TableCell colSpan={17} sx={{ p: 0, borderBottom: 0, backgroundColor: "rgba(25,118,210,.025)" }}>
                    <Button
                      fullWidth
                      startIcon={<AddCircleIcon />}
                      onClick={openAddClassDialog}
                      sx={{
                        justifyContent: "center", py: 1.35, borderRadius: 0, textTransform: "none",
                        fontWeight: 900, color: THEME_ORANGE, borderTop: `1px solid ${BORDER}`,
                        backgroundColor: "rgba(240,120,40,.035)",
                        "&:hover": { backgroundColor: "rgba(240,120,40,.085)" },
                      }}
                    >
                      Add New Class
                    </Button>
                  </TableCell>
                </TableRow>

                {filtersApplied && hasAnyFilterSelected && filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={16} sx={{ py: 3 }}>
                      <Alert
                        severity="warning"
                        sx={{
                          borderRadius: 2,
                          border: "1px solid rgba(255,152,0,0.25)",
                          backgroundColor: "rgba(255,152,0,0.05)",
                        }}
                      >
                        No results match the selected filters.
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {filtersApplied &&
            hasAnyFilterSelected &&
            filteredRows.length > 0 && (
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={page}
                onPageChange={(_, nextPage) =>
                  setPage(nextPage)
                }
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[100, 200, 300, 500, 1000, 2000, 5000]}
                labelRowsPerPage="Rows per page"
                showFirstButton
                showLastButton
                sx={{
                  borderTop:
                    "1px solid rgba(25,118,210,0.12)",
                  backgroundColor: "#fff",
                  "& .MuiTablePagination-toolbar": {
                    minHeight: 52,
                  },
                  "& .MuiTablePagination-selectLabel": {
                    fontWeight: 700,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    fontWeight: 700,
                  },
                }}
              />
            )}

          </Paper>
        </>
        )}

        {/* Bulk Create Classes Tab*/}
        {tab === 3 && (
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                Bulk Create Classes
              </Typography>

              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
                Select multiple SubCampus, Degree, Branch, Regulation, Semester, Subject Codes, and Sections.
                UniFLOO will create all valid class combinations and skip duplicates.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1.5,
                }}
              >
                <Autocomplete
                  options={academicyear}
                  value={bulkCreateForm.AcademicYear || null}
                  onChange={(_, value) =>
                    setBulkCreateForm((p) => ({ ...p, AcademicYear: value || "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Academic Year" size="small" sx={inputSx} />
                  )}
                />

                {[
                  ["SubCampusCode", "SubCampus", degreeSubjectMapLookups.SubCampusCode],
                  ["DegreeCode", "Degree", degreeSubjectMapLookups.DegreeCode],
                  ["BranchCode", "Branch", degreeSubjectMapLookups.BranchCode],
                  ["Regulation", "Regulation", degreeSubjectMapLookups.Regulation],
                  ["Semester", "Semester", degreeSubjectMapLookups.Semester],
                  ["Section", "Section", sectionOptionsFromTable],
                ].map(([field, label, list]) => {
                  const opts = withSelectAll(list || []);
                  const selected = opts.filter((x) =>
                    bulkCreateForm[field]?.includes(getOptionValue(x))
                  );
                
                  return (
                    <Autocomplete
                      key={field}
                      multiple
                      options={opts}
                      value={selected}
                      disableCloseOnSelect
                      getOptionLabel={getOptionLabelText}
                      isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
                      onChange={handleMultiSelectAll(field, list || [])}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={option.isSelectAll ? bulkCreateForm[field]?.length === list.length : selected} sx={checkboxSx} />
                          {getOptionLabelText(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label={`${label}(s)`} size="small" sx={inputSx} />
                      )}
                    />
                  );
                })}

                <Autocomplete
                  multiple
                  options={[
                    { SubjectCode: SELECT_ALL_VALUE, SubjectName: "Select All", isSelectAll: true },
                    ...bulkSubjects,
                  ]}
                  value={bulkSubjects.filter((x) =>
                    bulkCreateForm.SubjectCode.includes(x.SubjectCode)
                  )}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    option.isSelectAll
                      ? "Select All"
                      : `${option.SubjectCode} - ${option.SubjectName || option.SubjectNameCode || ""}`
                  }
                  isOptionEqualToValue={(a, b) => a.SubjectCode === b.SubjectCode}
                  onChange={(_, value) => {
                    const hasAll = value.some((x) => x.SubjectCode === SELECT_ALL_VALUE);
                    setBulkCreateForm((p) => ({
                      ...p,
                      SubjectCode: hasAll
                        ? bulkSubjects.map((x) => x.SubjectCode)
                        : value.map((x) => x.SubjectCode),
                    }));
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={
                          option.isSelectAll
                            ? bulkCreateForm.SubjectCode.length === bulkSubjects.length
                            : selected
                        }
                        sx={checkboxSx}
                      />
                      {option.isSelectAll
                        ? "Select All"
                        : `${option.SubjectCode} - ${option.SubjectName || option.SubjectNameCode || ""}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Subject Code(s)" size="small" sx={inputSx} 
                    helperText={bulkSubjects.length
                                ? "Only active subjects mapped to the selected scope are shown."
                                : "Select exactly one SubCampus, Degree, Branch, Regulation, and Semester to load subjects."} />
                  )}
                />

                <TextField
                  label="Room Number"
                  size="small"
                  value={bulkCreateForm.RoomNumber}
                  onChange={(e) =>
                    setBulkCreateForm((p) => ({ ...p, RoomNumber: e.target.value }))
                  }
                  sx={inputSx}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!bulkCreateForm.IsActive}
                      onChange={(e) =>
                        setBulkCreateForm((p) => ({
                          ...p,
                          IsActive: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label="Create as Active"
                />
              </Box>
                
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Example: 2 sections × 5 subjects = 10 classes. Existing duplicate classes will be skipped.
              </Alert>
                
              <Button
                variant="contained"
                sx={{ ...primaryBtnSx, mt: 2 }}
                startIcon={bulkCreating ? <CircularProgress size={18} color="inherit" /> : <AddCircleIcon />}
                disabled={bulkCreating}
                onClick={handleBulkCreateClasses}
              >
                {bulkCreating ? "Creating..." : "Bulk Create Classes"}
              </Button>

              {bulkCreateReport && (
                <Alert severity={bulkCreateReport.error ? "error" : bulkCreateReport.skipped ? "warning" : "success"} sx={{ mt: 2, borderRadius: 2 }}>
                  {bulkCreateReport.error ? (
                    bulkCreateReport.error
                  ) : (
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>
                        Created {bulkCreateReport.inserted || 0} class(es). Skipped {bulkCreateReport.skipped || 0}.
                      </Typography>
                      {!!bulkCreateReport.skipDetails?.length && (
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, maxHeight: 220, overflowY: "auto" }}>
                          {bulkCreateReport.skipDetails.map((d, i) => (
                            <li key={i}>
                              {d.reason}: {d.AcademicYear} / {d.SubCampusCode} / {d.DegreeCode} / {d.BranchCode} / {d.Regulation} / Sem {d.Semester} / {d.SubjectCode} / Sec {d.Section}
                            </li>
                          ))}
                        </Box>
                      )}
                      {bulkCreateReport.skipDetailsTruncated && (
                        <Typography variant="caption">Only the first {bulkCreateReport.skipDetails?.length || 0} skipped rows are shown.</Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          </Paper>
        )}


        {/* Bulk Assign Teachers / Class Representatives Tab */}
        {tab === 2 && (
          <>
            <Paper variant="outlined" sx={sectionSx}>
              <Box sx={{ pl: 1 }}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                  Bulk Assign Teachers / Class Representatives
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
                  Filter classes across one or many SubCampuses, select the exact class rows you want,
                  then assign one teacher or class representative to all selected classes in one operation.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(6, 1fr)" },
                    gap: 1,
                  }}
                >
                  {[
                    ["AcademicYear", "Academic Year", bulkAssignFilterOptions.AcademicYear],
                    ["SubCampusCode", "SubCampus", bulkAssignFilterOptions.SubCampusCode],
                    ["DegreeCode", "Degree", bulkAssignFilterOptions.DegreeCode],
                    ["BranchCode", "Branch", bulkAssignFilterOptions.BranchCode],
                    ["Regulation", "Regulation", bulkAssignFilterOptions.Regulation],
                    ["SemesterCode", "Semester", bulkAssignFilterOptions.SemesterCode],
                    ["Section", "Section", bulkAssignFilterOptions.Section],
                    ["SubjectCode", "Subject Code", bulkAssignFilterOptions.SubjectCode],
                    ["SubjectNameCode", "Subject Name Code", bulkAssignFilterOptions.SubjectNameCode],
                    ["TeacherName", "Teacher", bulkAssignFilterOptions.TeacherName],
                    ["ClassRepresentativeName", "Class Rep", bulkAssignFilterOptions.ClassRepresentativeName],
                    ["IsActive", "Status", bulkAssignFilterOptions.IsActive],
                  ].map(([field, label, list]) => {
                    const opts = withSelectAll(list || []);
                    const selected = opts.filter((x) =>
                      bulkAssignFilters[field]?.includes(getOptionValue(x))
                    );

                    return (
                      <Autocomplete
                        key={field}
                        multiple
                        options={opts}
                        value={selected}
                        disableCloseOnSelect
                        getOptionLabel={getOptionLabelText}
                        isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
                        onChange={(_, value) => {
                          const hasSelectAll = value.some(
                            (x) => getOptionValue(x) === SELECT_ALL_VALUE
                          );
                          setBulkAssignFilters((prev) => ({
                            ...prev,
                            [field]: hasSelectAll
                              ? list.map((x) => getOptionValue(x))
                              : value.map(getOptionValue),
                          }));
                          setBulkAssignFiltersApplied(false);
                          setBulkAssigneeId("");
                          setBulkAssignSelectedRows([]);
                        }}
                        renderOption={(props, option, { selected: optionSelected }) => (
                          <li {...props}>
                            <Checkbox
                              checked={
                                option.isSelectAll
                                  ? bulkAssignFilters[field]?.length === list.length && list.length > 0
                                  : optionSelected
                              }
                              sx={checkboxSx}
                            />
                            {getOptionLabelText(option)}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`${label}(s)`}
                            size="small"
                            sx={inputSx}
                          />
                        )}
                      />
                    );
                  })}
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    startIcon={<FilterAltIcon />}
                    onClick={handleApplyBulkAssignFilters}
                    disabled={!hasAnyBulkAssignFilter}
                  >
                    Filter Classes
                  </Button>
                  <Button
                    variant="outlined"
                    sx={dangerOutlinedBtnSx}
                    startIcon={<ClearIcon />}
                    onClick={handleClearBulkAssignFilters}
                  >
                    Clear
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {bulkAssignFiltersApplied && (
              <>
                <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
                  <Box sx={{ pl: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                          Select Classes
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                          {bulkAssignMatchedRows.length} matching class(es) across {bulkAssignSubCampuses.length} SubCampus(es). {bulkAssignSelectedRows.length} selected.
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          variant="outlined"
                          sx={outlinedBtnSx}
                          disabled={!bulkAssignMatchedRows.length}
                          onClick={handleSelectAllBulkAssignMatches}
                        >
                          Select All {bulkAssignMatchedRows.length}
                        </Button>
                        <Button
                          variant="outlined"
                          sx={dangerOutlinedBtnSx}
                          disabled={!bulkAssignSelectedRows.length}
                          onClick={handleClearBulkAssignSelection}
                        >
                          Clear Selection
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Paper>

                {bulkAssignMatchedRows.length > 0 ? (
                  <Paper elevation={0} sx={tableWrapSx}>
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={theadRowSx}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={bulkAssignAllPageSelected}
                                indeterminate={!bulkAssignAllPageSelected && bulkAssignSomePageSelected}
                                onChange={handleBulkAssignSelectAllPage}
                                sx={{
                                  ...checkboxSx,
                                  color: "rgba(255,255,255,0.85)",
                                  "&.Mui-checked": { color: "#fff" },
                                  "&.MuiCheckbox-indeterminate": { color: "#fff" },
                                }}
                              />
                            </TableCell>
                            <TableCell>Academic Year</TableCell>
                            <TableCell>SubCampus</TableCell>
                            <TableCell>Degree</TableCell>
                            <TableCell>Branch</TableCell>
                            <TableCell>Regulation</TableCell>
                            <TableCell>Semester</TableCell>
                            <TableCell>Section</TableCell>
                            <TableCell>Subject Code</TableCell>
                            <TableCell>Subject</TableCell>
                            <TableCell>Teacher</TableCell>
                            <TableCell>Class Rep</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bulkAssignPaginatedRows.map((row, idx) => (
                            <TableRow
                              key={row.ID}
                              sx={{
                                ...zebraRowSx(idx),
                                ...(bulkAssignSelectedRows.includes(row.ID)
                                  ? { backgroundColor: "rgba(240,120,40,0.10)" }
                                  : {}),
                              }}
                              onClick={() => {
                                setBulkAssignSelectedRows((prev) =>
                                  prev.includes(row.ID)
                                    ? prev.filter((id) => id !== row.ID)
                                    : [...prev, row.ID]
                                );
                              }}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={bulkAssignSelectedRows.includes(row.ID)}
                                  onChange={handleBulkAssignSelectRow(row.ID)}
                                  sx={checkboxSx}
                                />
                              </TableCell>
                              <TableCell>{row.AcademicYear}</TableCell>
                              <TableCell>{row.SubCampusCode}</TableCell>
                              <TableCell>{row.DegreeCode}</TableCell>
                              <TableCell>{row.BranchCode}</TableCell>
                              <TableCell>{row.Regulation}</TableCell>
                              <TableCell>{row.Semester}</TableCell>
                              <TableCell>{row.Section}</TableCell>
                              <TableCell>{row.SubjectCode}</TableCell>
                              <TableCell>{row.SubjectName || row.SubjectNameCode}</TableCell>
                              <TableCell>
                                {row.TeacherName || teacherNameFor(row.SubCampusCode, row.TeacherID) || "—"}
                              </TableCell>
                              <TableCell>
                                {row.ClassRepresentativeName ||
                                  classrepresentativeNameFor(row.SubCampusCode, row.ClassRepresentativeID) ||
                                  "—"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={Number(row.IsActive) === 1 ? "Active" : "Inactive"}
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      component="div"
                      count={bulkAssignMatchedRows.length}
                      page={bulkAssignPage}
                      onPageChange={(_, nextPage) => setBulkAssignPage(nextPage)}
                      rowsPerPage={bulkAssignRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setBulkAssignRowsPerPage(Number(e.target.value));
                        setBulkAssignPage(0);
                      }}
                      rowsPerPageOptions={[100, 200, 300]}
                      labelRowsPerPage="Rows per page"
                      showFirstButton
                      showLastButton
                      sx={{
                        borderTop: "1px solid rgba(25,118,210,0.12)",
                        backgroundColor: "#fff",
                        "& .MuiTablePagination-toolbar": { minHeight: 52 },
                        "& .MuiTablePagination-selectLabel": { fontWeight: 700 },
                        "& .MuiTablePagination-displayedRows": { fontWeight: 700 },
                      }}
                    />
                  </Paper>
                ) : (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    No classes match these filters.
                  </Alert>
                )}

                <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
                  <Box sx={{ pl: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      Assignment
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
                      The assignee list combines teachers / class representatives from every SubCampus represented by the filtered classes.
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 2fr auto" },
                        gap: 1.5,
                        alignItems: "start",
                      }}
                    >
                      <TextField
                        select
                        label="Assign"
                        size="small"
                        value={bulkAssignmentType}
                        onChange={(e) => setBulkAssignmentType(e.target.value)}
                        sx={inputSx}
                      >
                        <MenuItem value="TEACHER">Teacher</MenuItem>
                        <MenuItem value="CLASS_REP">Class Representative</MenuItem>
                      </TextField>

                      <Autocomplete
                        disabled={!bulkAssignSelectedRows.length}
                        options={bulkAssignmentType === "TEACHER" ? bulkTeacherOptions : bulkClassRepOptions}
                        value={
                          bulkAssignmentType === "TEACHER"
                            ? bulkTeacherOptions.find((x) => String(x._AssigneeKey) === String(bulkAssigneeId)) || null
                            : bulkClassRepOptions.find((x) => String(x._AssigneeKey) === String(bulkAssigneeId)) || null
                        }
                        getOptionLabel={(option) => {
                          if (bulkAssignmentType === "TEACHER") {
                            const name = option.TeacherName || String(option._AssigneeKey || "");
                            const subs = option._SubCampusCodes?.join(", ") || "";
                            return subs ? `${name} (${subs})` : name;
                          }
                          const name = option.ClassRepresentativeName || String(option._AssigneeKey || "");
                          const subs = option._SubCampusCodes?.join(", ") || "";
                          return subs ? `${name} (${subs})` : name;
                        }}
                        isOptionEqualToValue={(a, b) =>
                          String(a._AssigneeKey) === String(b._AssigneeKey)
                        }
                        onChange={(_, option) =>
                          setBulkAssigneeId(
                            option ? String(option._AssigneeKey) : ""
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={bulkAssignmentType === "TEACHER" ? "Teacher" : "Class Representative"}
                            size="small"
                            sx={inputSx}
                            helperText={
                              bulkAssignSelectedRows.length
                                ? `${bulkAssignSelectedRows.length} class(es) selected across ${uniqueSorted(
                                    bulkAssignMatchedRows
                                      .filter((r) => bulkAssignSelectedRows.includes(r.ID))
                                      .map((r) => r.SubCampusCode)
                                  ).length} SubCampus(es).`
                                : "Select one or more class rows first."
                            }
                          />
                        )}
                      />

                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        disabled={bulkAssigning || !bulkAssignSelectedRows.length || !bulkAssigneeId}
                        onClick={handleBulkAssignment}
                      >
                        {bulkAssigning
                          ? "Assigning..."
                          : `Assign to ${bulkAssignSelectedRows.length} Class(es)`}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </>
            )}
          </>
        )}

        {tab === 1 && (
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                Copy Classes to New Academic Year
              </Typography>
        
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
                Dropdowns include both active and inactive existing classes from the selected From Academic Year.
              </Typography>
        
              <Divider sx={{ my: 2 }} />
        
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1.5,
                }}
              >
                <Autocomplete
                  options={uniqueSorted(rows.map((r) => r.AcademicYear))}
                  value={copyForm.FromAcademicYear || null}
                  onChange={(_, value) =>
                    setCopyForm((p) => ({
                      ...p,
                      FromAcademicYear: value || "",
                      SubCampusCode: [],
                      DegreeCode: [],
                      BranchCode: [],
                      Regulation: [],
                      Semester: [],
                      SubjectCode: [],
                      Section: [],
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="From Academic Year" size="small" sx={inputSx} />
                  )}
                />

                <Autocomplete
                  freeSolo
                  options={academicyear}
                  value={copyForm.ToAcademicYear || ""}
                  onChange={(_, value) =>
                    setCopyForm((p) => ({ ...p, ToAcademicYear: value || "" }))
                  }
                  onInputChange={(_, value) =>
                    setCopyForm((p) => ({ ...p, ToAcademicYear: value || "" }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="To Academic Year"
                      size="small"
                      placeholder="2026-27"
                      sx={inputSx}
                    />
                  )}
                />

                {[
                  ["SubCampusCode", "SubCampus", copyOptions.SubCampusCode],
                  ["DegreeCode", "Degree", copyOptions.DegreeCode],
                  ["BranchCode", "Branch", copyOptions.BranchCode],
                  ["Regulation", "Regulation", copyOptions.Regulation],
                  ["Semester", "Semester", copyOptions.Semester],
                ].map(([field, label, list]) => {
                  const opts = withSelectAll(list || []);
                  const selected = opts.filter((x) =>
                    copyForm[field]?.includes(getOptionValue(x))
                  );
                
                  return (
                    <Autocomplete
                      key={field}
                      multiple
                      options={opts}
                      value={selected}
                      disableCloseOnSelect
                      getOptionLabel={getOptionLabelText}
                      isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
                      onChange={(_, value) => {
                        const hasSelectAll = value.some(
                          (x) => getOptionValue(x) === SELECT_ALL_VALUE
                        );
                      
                        setCopyForm((prev) => {
                          const next = {
                            ...prev,
                            [field]: hasSelectAll
                              ? list.map((x) => getOptionValue(x))
                              : value.map(getOptionValue),
                          };
                        
                          clearCopyDependents(field).forEach((f) => {
                            next[f] = [];
                          });
                        
                          return next;
                        });
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={
                              option.isSelectAll
                                ? copyForm[field]?.length === list.length && list.length > 0
                                : selected
                            }
                            sx={checkboxSx}
                          />
                          {getOptionLabelText(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${label}(s)`}
                          size="small"
                          sx={inputSx}
                          helperText={
                            !copyForm.FromAcademicYear
                              ? "Select From Academic Year first."
                              : !list.length
                              ? "No active classes found for current selection."
                              : ""
                          }
                        />
                      )}
                    />
                  );
                })}

                <Autocomplete
                  multiple
                  options={[
                    { SubjectCode: SELECT_ALL_VALUE, SubjectName: "Select All", isSelectAll: true },
                    ...copySubjectOptions,
                  ]}
                  value={copySubjectOptions.filter((x) =>
                    copyForm.SubjectCode.includes(x.SubjectCode)
                  )}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    option.isSelectAll
                      ? "Select All"
                      : `${option.SubjectCode} - ${option.SubjectName || option.SubjectNameCode || ""}`
                  }
                  isOptionEqualToValue={(a, b) => a.SubjectCode === b.SubjectCode}
                  onChange={(_, value) => {
                    const hasAll = value.some((x) => x.SubjectCode === SELECT_ALL_VALUE);
                    setCopyForm((p) => ({
                      ...p,
                      SubjectCode: hasAll
                        ? copySubjectOptions.map((x) => x.SubjectCode)
                        : value.map((x) => x.SubjectCode),
                      Section: [],
                    }));
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        checked={
                          option.isSelectAll
                            ? copyForm.SubjectCode.length === copySubjectOptions.length &&
                              copySubjectOptions.length > 0
                            : selected
                        }
                        sx={checkboxSx}
                      />
                      {option.isSelectAll
                        ? "Select All"
                        : `${option.SubjectCode} - ${option.SubjectName || option.SubjectNameCode || ""}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subject(s)"
                      size="small"
                      sx={inputSx}
                      helperText={
                        copySubjectOptions.length
                          ? "Subjects are loaded from existing classes, including inactive historical classes."
                          : "Select scope values to load subjects."
                      }
                    />
                  )}
                />

                {(() => {
                  const list = copyOptions.Section || [];
                  const opts = withSelectAll(list);
                  const selected = opts.filter((x) =>
                    copyForm.Section?.includes(getOptionValue(x))
                  );
                
                  return (
                    <Autocomplete
                      multiple
                      options={opts}
                      value={selected}
                      disableCloseOnSelect
                      getOptionLabel={getOptionLabelText}
                      isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
                      onChange={handleCopyMultiSelectAll("Section", list)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            checked={
                              option.isSelectAll
                                ? copyForm.Section?.length === list.length && list.length > 0
                                : selected
                            }
                            sx={checkboxSx}
                          />
                          {getOptionLabelText(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Section(s)" size="small" sx={inputSx} />
                      )}
                    />
                  );
                })()}
              </Box>
              
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={copyForm.CopyTeacher}
                      onChange={(e) =>
                        setCopyForm((p) => ({ ...p, CopyTeacher: e.target.checked }))
                      }
                    />
                  }
                  label="Copy Teachers"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={copyForm.CopyRoom}
                      onChange={(e) =>
                        setCopyForm((p) => ({ ...p, CopyRoom: e.target.checked }))
                      }
                    />
                  }
                  label="Copy Rooms"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={copyForm.CopyClassRep}
                      onChange={(e) =>
                        setCopyForm((p) => ({ ...p, CopyClassRep: e.target.checked }))
                      }
                    />
                  }
                  label="Copy Class Reps"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={copyForm.ActivateNew}
                      onChange={(e) =>
                        setCopyForm((p) => ({ ...p, ActivateNew: e.target.checked }))
                      }
                    />
                  }
                  label="Activate New Classes"
                />
              </Stack>
                
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Example: If From Year has K1 only with B.TECH, Degree shows only B.TECH. If you also select KK and KK has M.TECH, M.TECH appears.
              </Alert>
                
              <Button
                variant="contained"
                sx={{ ...primaryBtnSx, mt: 2 }}
                startIcon={<ContentCopyIcon />}
                disabled={copying}
                onClick={handleCopyAcademicYear}
              >
                {copying ? "Creating..." : "Create Classes for New Academic Year"}
              </Button>

              {copyReport && (
                <Alert severity={copyReport.error ? "error" : (copyReport.skipped || copyReport.totalSource === 0) ? "warning" : "success"} sx={{ mt: 2, borderRadius: 2 }}>
                  {copyReport.error ? (
                    copyReport.error
                  ) : (
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>
                        Source {copyReport.totalSource || 0}. Created {copyReport.inserted || 0}. Skipped {copyReport.skipped || 0}.
                      </Typography>
                      {!!copyReport.skipDetails?.length && (
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, maxHeight: 220, overflowY: "auto" }}>
                          {copyReport.skipDetails.map((d, i) => (
                            <li key={i}>
                              {d.reason}: {d.AcademicYear} / {d.SubCampusCode} / {d.DegreeCode} / {d.BranchCode} / {d.Regulation} / Sem {d.Semester} / {d.SubjectCode} / Sec {d.Section}
                            </li>
                          ))}
                        </Box>
                      )}
                      {copyReport.skipDetailsTruncated && (
                        <Typography variant="caption">Only the first {copyReport.skipDetails?.length || 0} skipped rows are shown.</Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          </Paper>
        )}


        {tab === 4 && (
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Bulk Delete Classes</Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
                Filter classes, select the exact rows to delete, then confirm the bulk deletion. Classes with dependent records are skipped by the backend.
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(6, 1fr)" }, gap: 1 }}>
                {[
                  ["AcademicYear", "Academic Year", bulkDeleteFilterOptions.AcademicYear],
                  ["SubCampusCode", "SubCampus", bulkDeleteFilterOptions.SubCampusCode],
                  ["DegreeCode", "Degree", bulkDeleteFilterOptions.DegreeCode],
                  ["BranchCode", "Branch", bulkDeleteFilterOptions.BranchCode],
                  ["Regulation", "Regulation", bulkDeleteFilterOptions.Regulation],
                  ["SemesterCode", "Semester", bulkDeleteFilterOptions.SemesterCode],
                  ["Section", "Section", bulkDeleteFilterOptions.Section],
                  ["SubjectCode", "Subject Code", bulkDeleteFilterOptions.SubjectCode],
                  ["SubjectNameCode", "Subject Name Code", bulkDeleteFilterOptions.SubjectNameCode],
                  ["TeacherName", "Teacher", bulkDeleteFilterOptions.TeacherName],
                  ["ClassRepresentativeName", "Class Rep", bulkDeleteFilterOptions.ClassRepresentativeName],
                  ["IsActive", "Status", bulkDeleteFilterOptions.IsActive],
                ].map(([field, label, list]) => {
                  const opts = withSelectAll(list || []);
                  const selected = opts.filter((x) => bulkDeleteFilters[field]?.includes(getOptionValue(x)));
                  return (
                    <Autocomplete
                      key={field}
                      multiple
                      options={opts}
                      value={selected}
                      disableCloseOnSelect
                      getOptionLabel={getOptionLabelText}
                      isOptionEqualToValue={(a,b) => getOptionValue(a) === getOptionValue(b)}
                      onChange={(_, value) => {
                        const hasAll = value.some((x) => getOptionValue(x) === SELECT_ALL_VALUE);
                        setBulkDeleteFilters((prev) => ({
                          ...prev,
                          [field]: hasAll ? list.map((x) => getOptionValue(x)) : value.map(getOptionValue),
                        }));
                        setBulkDeleteFiltersApplied(false);
                        setBulkDeleteSelectedRows([]);
                      }}
                      renderOption={(props, option, { selected: isSelected }) => (
                        <li {...props}>
                          <Checkbox checked={option.isSelectAll ? bulkDeleteFilters[field]?.length === list.length && list.length > 0 : isSelected} sx={checkboxSx} />
                          {getOptionLabelText(option)}
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} label={`${label}(s)`} size="small" sx={inputSx} />}
                    />
                  );
                })}
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
                <Button variant="contained" sx={primaryBtnSx} disabled={!hasAnyBulkDeleteFilter} onClick={() => { setBulkDeleteFiltersApplied(true); setBulkDeleteSelectedRows([]); setBulkDeletePage(0); }}>Filter</Button>
                <Button variant="outlined" sx={outlinedBtnSx} onClick={() => { setBulkDeleteFilters(emptyBulkAssignFilters); setBulkDeleteFiltersApplied(false); setBulkDeleteSelectedRows([]); setBulkDeleteReport(null); }}>Clear</Button>
              </Stack>

              {bulkDeleteFiltersApplied && (
                bulkDeleteMatchedRows.length ? (
                  <Paper elevation={0} sx={{ ...tableWrapSx, mt: 2 }}>
                    <Box sx={{ px: 2, py: 1.25, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", alignItems: "center", borderBottom: `1px solid ${BORDER}` }}>
                      <Typography sx={{ fontWeight: 800 }}>{bulkDeleteMatchedRows.length} class(es) matched · {bulkDeleteSelectedRows.length} selected</Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" variant="outlined" sx={outlinedBtnSx} onClick={() => setBulkDeleteSelectedRows(bulkDeleteMatchedRows.map((r) => r.ID))}>Select All Matches</Button>
                        <Button size="small" variant="outlined" sx={outlinedBtnSx} onClick={() => setBulkDeleteSelectedRows([])}>Clear Selection</Button>
                      </Box>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow sx={theadRowSx}>
                          <TableCell padding="checkbox"><Checkbox checked={paginatedBulkDeleteRows.length > 0 && paginatedBulkDeleteRows.every((r) => bulkDeleteSelectedRows.includes(r.ID))} indeterminate={paginatedBulkDeleteRows.some((r) => bulkDeleteSelectedRows.includes(r.ID)) && !paginatedBulkDeleteRows.every((r) => bulkDeleteSelectedRows.includes(r.ID))} onChange={(e) => { const ids=paginatedBulkDeleteRows.map((r)=>r.ID); setBulkDeleteSelectedRows((prev)=>e.target.checked ? [...new Set([...prev,...ids])] : prev.filter((id)=>!ids.includes(id))); }} sx={{ ...checkboxSx, color: "white", "&.Mui-checked": { color: "white" } }} /></TableCell>
                          <TableCell>Academic Year</TableCell><TableCell>SubCampus</TableCell><TableCell>Degree</TableCell><TableCell>Branch</TableCell><TableCell>Regulation</TableCell><TableCell>Semester</TableCell><TableCell>Section</TableCell><TableCell>Subject</TableCell><TableCell>Teacher</TableCell><TableCell>Class Rep</TableCell>
                        </TableRow></TableHead>
                        <TableBody>{paginatedBulkDeleteRows.map((row, idx) => (
                          <TableRow key={row.ID} sx={zebraRowSx(idx)}>
                            <TableCell padding="checkbox"><Checkbox checked={bulkDeleteSelectedRows.includes(row.ID)} onChange={(e) => setBulkDeleteSelectedRows((prev) => e.target.checked ? [...prev,row.ID] : prev.filter((id)=>id!==row.ID))} sx={checkboxSx} /></TableCell>
                            <TableCell>{row.AcademicYear}</TableCell><TableCell>{row.SubCampusCode}</TableCell><TableCell>{row.DegreeCode}</TableCell><TableCell>{row.BranchCode}</TableCell><TableCell>{row.Regulation}</TableCell><TableCell>{row.Semester}</TableCell><TableCell>{row.Section}</TableCell><TableCell>{row.SubjectCode}</TableCell><TableCell>{row.TeacherName || teacherNameFor(row.SubCampusCode,row.TeacherID) || "—"}</TableCell><TableCell>{row.ClassRepresentativeName || classrepresentativeNameFor(row.SubCampusCode,row.ClassRepresentativeID) || "—"}</TableCell>
                          </TableRow>
                        ))}</TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination component="div" count={bulkDeleteMatchedRows.length} page={bulkDeletePage} onPageChange={(_,p)=>setBulkDeletePage(p)} rowsPerPage={bulkDeleteRowsPerPage} onRowsPerPageChange={(e)=>{setBulkDeleteRowsPerPage(Number(e.target.value));setBulkDeletePage(0);}} rowsPerPageOptions={[100,200,300]} showFirstButton showLastButton />
                  </Paper>
                ) : <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>No classes match these filters.</Alert>
              )}

              <Button variant="contained" color="error" startIcon={<DeleteIcon />} sx={{ mt: 2, borderRadius: 2, textTransform: "none", fontWeight: 900 }} disabled={bulkDeleting || !bulkDeleteSelectedRows.length} onClick={handleBulkDeleteClasses}>
                {bulkDeleting ? "Deleting..." : `Delete ${bulkDeleteSelectedRows.length} Selected Class(es)`}
              </Button>

              {bulkDeleteReport && (
                <Alert severity={bulkDeleteReport.error ? "error" : bulkDeleteReport.skipped ? "warning" : "success"} sx={{ mt: 2, borderRadius: 2 }}>
                  {bulkDeleteReport.error || `Deleted ${bulkDeleteReport.deleted || 0} class(es). Skipped ${bulkDeleteReport.skipped || 0}.`}
                  {!!bulkDeleteReport.skipDetails?.length && (
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                      {bulkDeleteReport.skipDetails.slice(0,50).map((d,i)=><li key={i}>{d.reason}: ClassID {d.ClassID}</li>)}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          </Paper>
        )}

        <Dialog
          open={classDialogOpen}
          onClose={classSaving ? undefined : handleCancelEdit}
          fullWidth
          maxWidth="lg"
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", border: `1px solid ${BORDER}`, boxShadow: "0 18px 55px rgba(15,23,42,.22)" } }}
          BackdropProps={{ sx: { backgroundColor: "rgba(15,23,42,.45)", backdropFilter: "blur(2px)" } }}
        >
          <DialogTitle sx={{ p: 0 }}>
            <Box sx={{ ...headerSx, borderRadius: 0, px: 3, py: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 900 }}>{classDialogMode === "add" ? "Add New Class" : "Edit Class"}</Typography>
                <Typography sx={{ fontSize: ".82rem", opacity: .9 }}>Class scope, subject, room and assignment details</Typography>
              </Box>
              <IconButton onClick={handleCancelEdit} disabled={classSaving} sx={{ color: "white" }}><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: BG, p: { xs: 2, sm: 3 }, pt: { xs: 2.5, sm: 3.5 } }}>
            {!!error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            <Paper elevation={0} sx={{ mt: 2, p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, border: `1px solid ${BORDER}`, borderTop: `3px solid ${THEME_ORANGE}`, backgroundColor: "white" }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: .5 }}>Class Details</Typography>
              <Typography sx={{ fontSize: ".82rem", color: "text.secondary", mb: 2 }}>Select the academic scope and subject, then optionally assign room, teacher and class representative.</Typography>
              <Divider sx={{ mb: 2.25 }} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
                {[
                  ["AcademicYear", "Academic Year", academicyear],
                  ["SubCampusCode", "SubCampus", subCampusCodes],
                  ["DegreeCode", "Degree", degreeCodes],
                  ["BranchCode", "Branch", branchCodes],
                  ["Regulation", "Regulation", regulations?.length ? regulations : options.Regulation],
                  ["Semester", "Semester", semesters],
                ].map(([field, label, list]) => (
                  <Autocomplete
                    key={field}
                    freeSolo
                    autoHighlight
                    options={(list || []).map(String)}
                    value={newRow[field] ? String(newRow[field]) : null}
                    onChange={(_, value) => handleNewRowChange(field, value || "")}
                    onInputChange={(_, value, reason) => {
                      if (reason === "input") handleNewRowChange(field, value || "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={label} size="small" sx={inputSx} fullWidth />
                    )}
                  />
                ))}

                <Autocomplete
                  freeSolo
                  autoHighlight
                  options={subjects}
                  value={subjects.find((x) => String(x.SubjectCode) === String(newRow.SubjectCode)) || (newRow.SubjectCode || null)}
                  getOptionLabel={(option) => typeof option === "string" ? option : `${option.SubjectCode} - ${option.SubjectName || option.SubjectNameCode || ""}`}
                  isOptionEqualToValue={(a, b) => String(a?.SubjectCode ?? a) === String(b?.SubjectCode ?? b)}
                  onChange={(_, value) => {
                    if (typeof value === "string") handleNewRowChange("SubjectCode", value);
                    else if (value) handleSubjectChange("SubjectCode", value.SubjectCode);
                    else handleNewRowChange("SubjectCode", "");
                  }}
                  onInputChange={(_, value, reason) => { if (reason === "input") handleNewRowChange("SubjectCode", value); }}
                  renderInput={(params) => <TextField {...params} label="Subject Code" size="small" fullWidth sx={inputSx} />}
                />
                <TextField label="Subject Name Code" size="small" value={newRow.SubjectNameCode || ""} disabled fullWidth sx={inputSx} />
                <TextField label="Subject Name" size="small" value={newRow.SubjectName || ""} disabled fullWidth sx={inputSx} />

                <Autocomplete
                  freeSolo
                  autoHighlight
                  options={classSectionOptions}
                  value={newRow.Section || null}
                  onChange={(_, value) => handleNewRowChange("Section", value || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") handleNewRowChange("Section", value || ""); }}
                  renderInput={(params) => <TextField {...params} label="Section" size="small" fullWidth sx={inputSx} helperText={classSectionOptions.length ? "Type or select a section." : "You can type a section value."} />}
                />
                <TextField label="Room Number" name="RoomNumber" size="small" value={newRow.RoomNumber || ""} onChange={handleInputChange} fullWidth sx={inputSx} />

                <Autocomplete
                  autoHighlight
                  disabled={!newRow.SubCampusCode}
                  options={currentTeacherOptions}
                  value={currentTeacherOptions.find((x) => String(x.value) === String(newRow.TeacherID ?? "")) || null}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
                  filterOptions={(opts, state) => {
                    const q = String(state.inputValue || "").trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((x) =>
                      String(x.TeacherName || "").toLowerCase().includes(q) ||
                      String(x.EmployeeID || "").toLowerCase().includes(q) ||
                      String(x.TeacherID || "").toLowerCase().includes(q)
                    );
                  }}
                  onChange={(_, option) => handleNewRowChange("TeacherID", option?.value || "")}
                  renderInput={(params) => <TextField {...params} label="Teacher" size="small" fullWidth sx={inputSx} placeholder="Search by name or employee ID" />}
                />

                <Autocomplete
                  autoHighlight
                  disabled={!newRow.SubCampusCode}
                  options={currentClassRepOptions}
                  value={currentClassRepOptions.find((x) => String(x.value) === String(newRow.ClassRepresentativeID ?? "")) || null}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
                  filterOptions={(opts, state) => {
                    const q = String(state.inputValue || "").trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((x) =>
                      String(x.ClassRepresentativeName || "").toLowerCase().includes(q) ||
                      String(x.StudentID || "").toLowerCase().includes(q) ||
                      String(x.StudentKey || "").toLowerCase().includes(q) ||
                      String(x.ClassRepresentativeID || "").toLowerCase().includes(q)
                    );
                  }}
                  onChange={(_, option) => handleNewRowChange("ClassRepresentativeID", option?.value || "")}
                  renderInput={(params) => <TextField {...params} label="Class Representative" size="small" fullWidth sx={inputSx} placeholder="Search by name or student ID" />}
                />
                <FormControlLabel
                  control={<Switch checked={!!newRow.IsActive} onChange={handleNewRowActiveToggle} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: THEME_ORANGE }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: THEME_ORANGE } }} />}
                  label={newRow.IsActive ? "Active" : "Inactive"}
                  sx={{ m: 0, px: 1.5, py: .75, borderRadius: 2, border: `1px solid ${BORDER}`, width: "fit-content" }}
                />
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button onClick={handleCancelEdit} disabled={classSaving} variant="outlined" sx={outlinedBtnSx}>Cancel</Button>
            <Button
              variant="contained"
              disabled={classSaving}
              onClick={async () => {
                setClassSaving(true);
                try {
                  if (classDialogMode === "add") await handleAddRow();
                  else await handleSaveRow(classDialogEditId);
                } finally {
                  setClassSaving(false);
                }
              }}
              sx={primaryBtnSx}
            >
              {classSaving ? "Saving..." : classDialogMode === "add" ? "Add Class" : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

</Box>
</Paper>
      </Paper>
    </Box>
  );
};

export default UpdateClasses;
